// jmix-exit E3a.3: the SPA message registry. The `menu.*` keys are ported verbatim from the Jmix
// bundles (src/main/resources/.../messages_{nl,en}.properties) so the sidebar reads identically to
// the legacy app; the `shell.*` / `state.*` keys are the new SPA chrome (shell + error/empty/loading
// conventions). Kept dependency-free (no i18next), matching backoffice's lightweight i18nProvider.
// Epic 5.4's "one key catalogue → MessageSource + i18next" infra later owns extraction across modules.

export type Locale = "nl" | "en";

type Bundle = Record<string, string>;

export const messages: Record<Locale, Bundle> = {
  nl: {
    // ── menu.xml (ported) ──────────────────────────────────────────────────
    "menu.application.title": "Pleeggezinportaal",
    "menu.dashboard": "Dashboard",
    "menu.myAnimals": "Mijn dieren",
    "menu.availability": "Beschikbaarheid",
    "menu.contact": "Contact",
    "menu.myProfile": "Mijn gegevens",
    "menu.selfservice.title": "Selfservice",
    "menu.selfservice.myServiceAccount": "Mijn serviceaccount",
    "menu.security.title": "Beveiliging",
    "menu.users": "Gebruikers",
    "menu.persons": "Personen",
    "menu.serviceAccounts": "Serviceaccounts",
    "menu.organizations": "Organisaties",
    "menu.organizationRotation": "Master key roteren",
    // ── shell chrome ───────────────────────────────────────────────────────
    "shell.brand": "Docasoft",
    "shell.applicationTitle": "Pleeggezinportaal",
    "shell.applicationTitle.selfservice": "Serviceaccount-portaal",
    "shell.applicationTitle.admin": "Beheer",
    "shell.profile.settings": "Instellingen",
    "shell.profile.logout": "Uitloggen",
    "shell.role.foster": "Pleegouder",
    "shell.role.admin": "Beheerder",
    "shell.role.self-service": "Serviceaccount",
    "shell.home.title": "Overzicht",
    "shell.home.body": "Welkom. Kies een onderdeel in het menu links.",
    "shell.underConstruction.title": "In aanbouw",
    "shell.underConstruction.body": "Dit scherm wordt in een volgende fase gebouwd.",
    "shell.underConstruction.home": "Naar overzicht",
    // ── auth screens (jmix-3b.1) ───────────────────────────────────────────
    "auth.admin.title": "Beheerder inloggen",
    "auth.admin.lede": "Log in met uw gebruikersnaam en wachtwoord.",
    "auth.admin.submit": "Inloggen",
    "auth.admin.submitting": "Bezig met inloggen…",
    "auth.admin.rememberMe": "Onthoud mij",
    "auth.foster.title": "Inloggen op het pleeggezinnenportaal",
    "auth.foster.eyebrow": "Voor pleeggezinnen van dierenasielen",
    "auth.foster.emailLede": "Vul je e-mailadres in en ontvang een inlogcode.",
    "auth.foster.trust": "Toegang voor pleeggezinnen",
    "auth.foster.requestCode": "Stuur code",
    "auth.foster.requesting": "Bezig met versturen…",
    "auth.foster.codeSentPrefix": "Als ",
    "auth.foster.codeSentSuffix":
      " bij ons bekend is, ontvang je binnen enkele minuten een e-mail met een inlogcode.",
    "auth.foster.codeTitle": "Voer je inlogcode in",
    "auth.foster.codeLede": "Vul de code uit de e-mail in.",
    "auth.foster.devCode": "Testomgeving — je inlogcode is:",
    "auth.foster.verify": "Inloggen",
    "auth.foster.verifying": "Bezig met controleren…",
    "auth.foster.welcomeBack": "Welkom terug.",
    "auth.foster.changeEmail": "Ander e-mailadres gebruiken",
    "auth.foster.resend": "Nieuwe code sturen",
    "auth.foster.resending": "Bezig met versturen…",
    // ── set-password (jmix-7.3, ported from the legacy SetPasswordView bundle) ──
    "auth.setPassword.title": "Nieuw wachtwoord instellen",
    "auth.setPassword.heading": "Stel een nieuw wachtwoord in",
    "auth.setPassword.intro":
      "Kies een wachtwoord van minimaal 8 tekens. Na opslaan kunt u met het nieuwe wachtwoord inloggen.",
    "auth.setPassword.submit": "Wachtwoord opslaan",
    "auth.setPassword.submitting": "Bezig met opslaan…",
    "auth.setPassword.success": "Wachtwoord ingesteld. U kunt nu inloggen.",
    "auth.setPassword.toLogin": "Naar inloggen",
    "auth.setPassword.error.uniform":
      "Link verlopen of ongeldig. Vraag de beheerder een nieuwe resetlink uit te geven.",
    "auth.setPassword.error.confirmMismatch": "De twee wachtwoorden komen niet overeen.",
    "auth.setPassword.error.weakPassword": "Het wachtwoord moet minimaal 8 tekens lang zijn.",
    "auth.setPassword.help.newPassword": "Minimaal 8 tekens.",
    "auth.field.username": "Gebruikersnaam",
    "auth.field.password": "Wachtwoord",
    "auth.field.newPassword": "Nieuw wachtwoord",
    "auth.field.confirmPassword": "Bevestig nieuw wachtwoord",
    "auth.field.email": "E-mailadres",
    "auth.field.code": "Inlogcode",
    "auth.field.revealPassword": "Wachtwoord tonen",
    "auth.field.hidePassword": "Wachtwoord verbergen",
    "auth.error.network": "Kan de server niet bereiken. Probeer het opnieuw.",
    "auth.error.generic": "Inloggen is mislukt. Probeer het opnieuw.",
    "auth.error.rateLimited": "Te veel pogingen. Wacht even en probeer het opnieuw.",
    "auth.error.invalidCredentials": "Ongeldige gebruikersnaam of wachtwoord.",
    "auth.error.invalidCode": "De code is onjuist of verlopen.",
    "auth.error.codeFormat": "De inlogcode bestaat uit zes cijfers.",
    "auth.error.enterEmail": "Vul een e-mailadres in.",
    "auth.error.enterCode": "Vul de inlogcode in.",
    "auth.error.enterCredentials": "Vul uw gebruikersnaam en wachtwoord in.",
    "auth.error.enterPassword": "Vul een nieuw wachtwoord in.",
    // ── error / empty / loading conventions (Epics 7–9 reuse) ──────────────
    "state.loading": "Laden…",
    "state.empty.title": "Niets te tonen",
    "state.empty.body": "Er zijn nog geen gegevens.",
    "state.error.title": "Er ging iets mis",
    "state.error.body": "Het scherm kon niet worden geladen. Probeer het opnieuw.",
    "state.error.retry": "Opnieuw proberen",
    "state.placeholder.note": "Dit onderdeel komt in een volgende migratiefase.",
    // ── nazorg adopter portal (aftercare questionnaire) ────────────────────
    "nazorg.portalName": "Nazorg na adoptie",
    "nazorg.fallbackName": "je nieuwe huisdier",
    "nazorg.saveLater": "Opslaan & later verder",
    "nazorg.savedToast": "Je antwoorden zijn opgeslagen — je kunt later verdergaan.",
    "nazorg.welcome.kicker": "Welkom terug",
    "nazorg.welcome.title": "Hoe gaat het met {naam}?",
    "nazorg.welcome.lede":
      "Fijn dat je {naam} een nieuw thuis hebt gegeven. We horen graag hoe het samen gaat — jouw antwoorden helpen ons de zorg te verbeteren.",
    "nazorg.welcome.count": "{n} korte vragen",
    "nazorg.welcome.duration": "± 3 minuten",
    "nazorg.welcome.savedNote": "We hebben je eerdere antwoorden bewaard.",
    "nazorg.start": "Beginnen",
    "nazorg.resume": "Verdergaan",
    "nazorg.context.kicker": "Jouw maatje",
    "nazorg.context.subtitle": "We volgen graag hoe het thuis met {naam} gaat.",
    "nazorg.context.sectionMeta": "{x} van {y} ingevuld",
    "nazorg.context.helpTitle": "Vragen of zorgen?",
    "nazorg.question": "Vraag",
    "nazorg.autosave": "Je antwoorden worden automatisch bewaard",
    "nazorg.next": "Volgende",
    "nazorg.back": "Terug",
    "nazorg.finish": "Afronden",
    "nazorg.onepage.subtitle": "Vul in wat je kwijt wilt — alle vragen zijn optioneel.",
    "nazorg.onepage.answered": "{x} / {total} beantwoord",
    "nazorg.submit": "Vragenlijst verzenden",
    "nazorg.submitting": "Versturen…",
    "nazorg.submitError": "Versturen is niet gelukt. Probeer het zo nog eens.",
    "nazorg.done.title": "Dankjewel!",
    "nazorg.done.lede":
      "Je antwoorden zijn verzonden. Heel fijn dat je de tijd nam om ons te laten weten hoe het met {naam} gaat. We nemen contact op als dat nodig is.",
    "nazorg.done.review": "Antwoorden opnieuw bekijken",
    "nazorg.expired.title": "Link niet meer geldig",
    "nazorg.expired.body":
      "Deze vragenlijst is al ingevuld of de link is verlopen. Neem contact op met de opvang als je vragen hebt.",
    "nazorg.foto.choose": "Foto kiezen",
    "nazorg.foto.replace": "Andere foto kiezen",
    "nazorg.foto.remove": "Verwijderen",
    "nazorg.foto.uploading": "Uploaden…",
    "nazorg.foto.previewAlt": "Gekozen foto",
    "nazorg.foto.error": "Uploaden is niet gelukt. Kies een JPEG- of PNG-foto (max 8 MB).",
    // ── foster portal — shared (jmix-8) ────────────────────────────────────
    "portal.backofficeUnavailable":
      "De gegevens van de organisatie zijn momenteel niet bereikbaar. Probeer het later opnieuw.",
    // ── 8.1 dashboard ──────────────────────────────────────────────────────
    "portal.dashboard.welcome": "Welkom",
    "portal.dashboard.subtitle":
      "Fijn dat je er bent! Hieronder vind je de dieren die je op dit moment verzorgt.",
    "portal.dashboard.animals.title": "Mijn dieren",
    "portal.dashboard.animals.empty": "Je hebt op dit moment geen dieren in opvang.",
    "portal.dashboard.since": "Sinds",
    "portal.dashboard.updates.title": "Recente updates",
    "portal.dashboard.updates.empty": "Nog geen recente updates.",
    "portal.dashboard.updates.weight": "Gewicht",
    "portal.dashboard.updates.unit.kg": "kg",
    "portal.dashboard.updates.byPerson": "Door jou",
    "portal.dashboard.updates.byOrganization": "Door de organisatie",
    "portal.dashboard.allAnimals": "Naar alle mijn dieren",
    "portal.dashboard.allUpdates": "Naar alle updates",
    "portal.dashboard.viewDetails": "Bekijk details",
    "portal.dashboard.vestiging": "Vestiging",
    "portal.dashboard.time.today": "Vandaag",
    "portal.dashboard.time.yesterday": "Gisteren",
    "portal.dashboard.availability.title": "Beschikbaarheid",
    "portal.dashboard.availability.next": "Eerstvolgende afwezigheid",
    "portal.dashboard.availability.none": "Geen geplande afwezigheid.",
    "portal.dashboard.availability.planned": "Gepland",
    "portal.dashboard.availability.allOrganizations": "Geldt voor alle organisaties",
    "portal.dashboard.availability.add": "Nieuwe afwezigheid toevoegen",
    "portal.dashboard.contact.title": "Contact organisatie",
    "portal.dashboard.contact.unavailable": "Contactgegevens zijn momenteel niet beschikbaar.",
    "portal.dashboard.contact.heading": "Contact & spoed",
    "portal.dashboard.contact.daytime": "Contact overdag",
    "portal.dashboard.allLocations": "Alle vestigingen",
    "portal.dashboard.updates.add": "Nieuwe update doorgeven",
    "portal.dashboard.availability.toAvailability": "Naar beschikbaarheid",
    "portal.dashboard.thanks.title": "Bedankt!",
    "portal.dashboard.thanks.body":
      "Dankzij jouw inzet krijgen deze dieren de beste zorg en kans op een mooi nieuw thuis.",
    // ── 8.2 mijn dieren ────────────────────────────────────────────────────
    "portal.animals.empty": "Je hebt op dit moment geen dieren in opvang.",
    "portal.animals.photoAlt": "Foto van het dier",
    "portal.animals.photoAltNamed": "Foto van ",
    "portal.animals.dobLabel": "Geboortedatum",
    "portal.animals.chipLabel": "Chipnummer",
    "portal.animals.title": "Mijn dieren",
    "portal.animals.subtitle": "Hieronder zie je alle dieren die op dit moment bij jou in de opvang zijn.",
    "portal.animals.stat.total": "Totaal dieren",
    "portal.animals.stat.lastUpdated": "Laatst bijgewerkt",
    "portal.animals.search": "Zoek op naam of ras…",
    "portal.animals.filter.allSpecies": "Alle dieren",
    "portal.animals.sort.newest": "Sorteren: Nieuwste eerst",
    "portal.animals.sort.oldest": "Sorteren: Oudste eerst",
    "portal.animals.sort.name": "Sorteren: Naam A–Z",
    "portal.animals.status.since": "Opvang sinds ",
    "portal.animals.open": "Openen",
    "portal.animals.noResults": "Geen dieren gevonden.",
    "portal.error.title": "Oeps, er ging iets technisch mis",
    "portal.error.body1": "We hebben de foutmelding in onderzoek.",
    "portal.error.body2": "Probeer het gerust opnieuw.",
    "portal.error.retry": "Opnieuw proberen",
    "portal.error.home": "Terug naar dashboard",
    "portal.error.footerPrefix": "Lukt het nog steeds niet? Neem dan",
    "portal.error.contactLink": "contact met ons op",
    // ── 8.5 dier-detail ────────────────────────────────────────────────────
    "portal.animalDetail.back": "Terug naar mijn dieren",
    "portal.animalDetail.nav.prev": "Vorig dier",
    "portal.animalDetail.nav.next": "Volgend dier",
    "portal.animalDetail.nav.of": "van",
    "portal.animalDetail.tabs.label": "Onderdelen",
    "portal.animalDetail.tabs.overview": "Overzicht",
    "portal.animalDetail.tabs.care": "Verzorging",
    "portal.animalDetail.tabs.medical": "Medisch",
    "portal.animalDetail.tabs.notes": "Notities",
    "portal.animalDetail.tabs.log": "Logboek",
    "portal.animalDetail.actions.title": "Acties",
    "portal.animalDetail.actions.addNote": "Nieuwe notitie",
    "portal.animalDetail.actions.addWeight": "Gewicht toevoegen",
    "portal.animalDetail.actions.addFeeding": "Voeding registreren",
    "portal.animalDetail.actions.addVacc": "Nieuwe enting",
    "portal.animalDetail.actions.editFeeding": "Voedingsschema bewerken",
    "portal.animalDetail.stat.stayDuration": "Verblijfduur",
    "portal.animalDetail.stat.day": "dag",
    "portal.animalDetail.stat.days": "dagen",
    "portal.animalDetail.overview.weight": "Gewicht",
    "portal.animalDetail.overview.latestMeasurement": "Laatste meting",
    "portal.animalDetail.overview.weightValue": "Gewicht",
    "portal.animalDetail.overview.medical": "Medische aandachtspunten",
    "portal.animalDetail.overview.medicalEmpty": "Geen entingen of behandelingen geregistreerd.",
    "portal.animalDetail.overview.recentNotes": "Recente notities",
    "portal.animalDetail.overview.seeAllMedical": "Bekijk alles",
    "portal.animalDetail.overview.seeAllNotes": "Bekijk alle",
    "portal.animalDetail.overview.last": "Laatste:",
    "portal.animalDetail.log.title": "Logboek",
    "portal.animalDetail.log.empty": "Er is nog niets vastgelegd voor dit dier.",
    "portal.animalDetail.log.kind.note": "Notitie",
    "portal.animalDetail.log.kind.weight": "Gewicht gemeten",
    "portal.animalDetail.log.kind.feeding": "Voeding",
    "portal.animalDetail.log.kind.vaccination": "Enting / behandeling",
    "portal.animalDetail.tile.basis": "Basisinformatie",
    "portal.animalDetail.tile.stay": "Verblijf",
    "portal.animalDetail.tile.weights": "Gewichten",
    "portal.animalDetail.tile.notes": "Notities",
    "portal.animalDetail.tile.protection": "Bescherming",
    "portal.animalDetail.tile.feeding": "Voeding",
    "portal.animalDetail.tile.media": "Media & Documenten",
    "portal.animalDetail.notAvailable": "Nog niet beschikbaar via het portaal",
    "portal.animalDetail.field.species": "Diersoort",
    "portal.animalDetail.field.breed": "Ras",
    "portal.animalDetail.field.sex": "Geslacht",
    "portal.animalDetail.field.dob": "Geboortedatum",
    "portal.animalDetail.field.chip": "Chipnummer",
    "portal.animalDetail.stay.since": "Sinds",
    "portal.animalDetail.stay.until": "Tot",
    "portal.animalDetail.stay.open": "heden",
    "portal.animalDetail.weights.empty": "Nog geen gewichten geregistreerd.",
    "portal.animalDetail.weights.unit": "kg",
    "portal.animalDetail.weights.add": "Gewicht toevoegen",
    "portal.animalDetail.weights.kg": "Gewicht (kg)",
    "portal.animalDetail.weights.kgPlaceholder": "bijv. 17,5 of 17.5",
    "portal.animalDetail.weights.latest": "Laatste meting:",
    "portal.animalDetail.weights.columnKg": "Gewicht (kg)",
    "portal.animalDetail.weights.columnSource": "Bron",
    "portal.animalDetail.weights.period3m": "Laatste 3 maanden",
    "portal.animalDetail.weights.period1m": "Laatste maand",
    "portal.animalDetail.weights.periodAll": "Alles",
    "portal.animalDetail.weights.periodFew": "Te weinig metingen in deze periode — alle metingen getoond.",
    "portal.animalDetail.notes.empty": "Nog geen notities.",
    "portal.animalDetail.notes.addNew": "Nieuwe notitie",
    "portal.animalDetail.notes.titleLabel": "Titel",
    "portal.animalDetail.notes.textLabel": "Notitie",
    "portal.animalDetail.notes.pinned": "Vastgepind",
    "portal.animalDetail.notes.tagsLabel": "Tags",
    "portal.animalDetail.notes.add": "Notitie toevoegen",
    "portal.animalDetail.notes.delete": "Verwijderen",
    "portal.animalDetail.notes.deleteConfirm": "Weet je zeker dat je deze notitie wilt verwijderen?",
    "portal.animalDetail.notes.titlePlaceholder": "Titel",
    "portal.animalDetail.notes.textPlaceholder": "Notitie…",
    "portal.animalDetail.bySource.person": "Door jou",
    "portal.animalDetail.bySource.organization": "Door de organisatie",
    "portal.animalDetail.tile.tasks": "Taken & aandachtspunten",
    "portal.animalDetail.tile.registrations": "Registraties & Wizards",
    "portal.animalDetail.field.name": "Naam",
    "portal.animalDetail.field.caster": "Castratiestatus",
    "portal.animalDetail.field.castrationDate": "Castratiedatum",
    "portal.animalDetail.field.coat": "Vacht",
    "portal.animalDetail.field.coatColor": "Vachtkleur",
    "portal.animalDetail.field.passport": "Paspoortnummer",
    "portal.animalDetail.field.pedigree": "Stamboomnummer",
    "portal.animalDetail.field.deceased": "Datum overleden",
    "portal.animalDetail.field.features": "Uiterlijke kenmerken",
    "portal.animalDetail.field.tags": "Tags",
    "portal.animalDetail.field.since": "In opvang sinds",
    "portal.animalDetail.age.years": "j",
    "portal.animalDetail.age.months": "mnd",
    "portal.animalDetail.yes": "Ja",
    "portal.animalDetail.no": "Nee",
    "portal.animalDetail.field.crossbreed": "Kruising",
    "portal.animalDetail.protection.empty": "Geen registraties.",
    "portal.animalDetail.protection.next": "Volgende:",
    "portal.animalDetail.protection.givenBy": "Door:",
    "portal.animalDetail.protection.status.actueel": "Actueel",
    "portal.animalDetail.protection.status.binnenkort": "Binnenkort",
    "portal.animalDetail.protection.status.verlopen": "Verlopen",
    "portal.animalDetail.protection.status.onbekend": "Onbekend",
    "portal.animalDetail.edit.edit": "Bewerken",
    "portal.animalDetail.edit.save": "Opslaan",
    "portal.animalDetail.edit.saving": "Opslaan…",
    "portal.animalDetail.edit.cancel": "Annuleren",
    "portal.animalDetail.edit.error": "Opslaan is mislukt. Probeer het opnieuw.",
    "portal.animalDetail.feeding.empty": "Geen voedingsschema beschikbaar.",
    "portal.animalDetail.feeding.feedType": "Voertype",
    "portal.animalDetail.feeding.brand": "Merk",
    "portal.animalDetail.feeding.amount": "Hoeveelheid",
    "portal.animalDetail.feeding.times": "Voertijden",
    "portal.animalDetail.feeding.diet": "Dieet",
    "portal.animalDetail.feeding.note": "Opmerking",
    "portal.animalDetail.feeding.log": "Voedingslog",
    "portal.animalDetail.feeding.editSchema": "Schema bewerken",
    "portal.animalDetail.feeding.addLog": "Toevoegen",
    "portal.animalDetail.feeding.column.date": "Datum",
    "portal.animalDetail.feeding.amountGrams": "Hoeveelheid (gram)",
    "portal.animalDetail.feeding.behavior": "Gedrag",
    "portal.animalDetail.feeding.behaviorPlaceholder": "Gedrag — maak een keuze",
    "portal.animalDetail.feeding.amountRequired": "Vul een hoeveelheid (gram) groter dan 0 in.",
    "portal.animalDetail.feeding.logDelete": "Verwijderen",
    "portal.animalDetail.feeding.logDeleteConfirm": "Weet je zeker dat je deze logregel wilt verwijderen?",
    "portal.animalDetail.media.empty": "Geen media of documenten.",
    "portal.animalDetail.media.description": "Omschrijving",
    "portal.animalDetail.media.delete": "Verwijderen",
    "portal.animalDetail.media.deleteConfirm": "Weet je zeker dat je dit bestand wilt verwijderen?",
    "portal.animalDetail.vacc.add": "Inenting toevoegen",
    "portal.animalDetail.vacc.delete": "Verwijderen",
    "portal.animalDetail.vacc.deleteConfirm": "Weet je zeker dat je deze inenting wilt verwijderen?",
    "portal.animalDetail.vacc.name": "Type",
    "portal.animalDetail.vacc.brand": "Product/Merk",
    "portal.animalDetail.vacc.date": "Gegeven op",
    "portal.animalDetail.vacc.nextDate": "Volgende op",
    "portal.animalDetail.vacc.addEnting": "Nieuwe enting",
    "portal.animalDetail.vacc.addParasite": "Nieuwe ontworm/ontvlooiing",
    "portal.animalDetail.vacc.parasitePreset": "Ontwormen/ontvlooien",
    "portal.animalDetail.vacc.filter.all": "Alle",
    "portal.animalDetail.vacc.filter.enting": "Enting",
    "portal.animalDetail.vacc.filter.ontwormen": "Ontwormen",
    "portal.animalDetail.vacc.filter.ontvlooien": "Ontvlooien",
    "portal.animalDetail.vacc.filter.search": "Zoeken...",
    "portal.animalDetail.vacc.filter.clear": "Filters wissen",
    "portal.animalDetail.vacc.filter.none": "Geen resultaten voor deze filters.",
    "portal.animalDetail.vacc.aiTitle": "Paspoort uitlezen met AI",
    "portal.animalDetail.vacc.aiHint": "Upload foto's of pdf's van het dierenpaspoort; de AI leest de entingen voor je uit.",
    "portal.animalDetail.vacc.aiRead": "Lees alle entingen (AI)",
    "portal.animalDetail.vacc.aiClear": "Uploads wissen",
    "portal.animalDetail.vacc.aiBusy": "Bezig met lezen...",
    "portal.animalDetail.vacc.aiImport": "Importeren",
    "portal.animalDetail.vacc.aiNone": "Geen entingen gevonden op de pagina's.",
    "portal.animalDetail.vacc.aiNotConfigured": "De AI-lezer is niet geconfigureerd op deze omgeving.",
    "portal.animalDetail.vacc.aiError": "Het uitlezen is niet gelukt. Probeer het opnieuw.",
    "portal.animalDetail.vacc.aiFoundPrefix": "Gevonden:",
    "portal.animalDetail.vacc.aiFoundSuffix": "regels. Vink aan wat je wilt importeren.",
    "portal.animalDetail.wizards.empty": "Geen wizards beschikbaar.",
    "portal.animalDetail.wizards.start": "Starten",
    "portal.animalDetail.wizards.started": "✓ Gestart",
    "portal.animalDetail.wizards.finished2": "✓ Afgerond",
    "portal.animalDetail.wizards.previous": "Vorige",
    "portal.animalDetail.wizards.next": "Volgende",
    "portal.animalDetail.wizards.finish": "Afronden",
    "portal.animalDetail.wizards.saveError": "Opslaan is niet gelukt. Probeer het opnieuw.",
    "portal.animalDetail.wizards.required": "Vul dit veld in:",
    "portal.animalDetail.wizards.requiredSuffix": "is verplicht.",
    "portal.animalDetail.wizards.noSchema": "Deze wizard heeft (nog) geen vragen.",
    "portal.animalDetail.wizards.noFields": "Geen velden in deze stap.",
    "portal.animalDetail.wizards.yes": "Ja",
    "portal.animalDetail.wizards.no": "Nee",
    "portal.animalDetail.wizards.run": "uitvoeren",
    "portal.animalDetail.wizards.saveDraft": "Opslaan als concept",
    "portal.animalDetail.wizards.side.chip": "Chipnummer",
    "portal.animalDetail.wizards.side.birth": "Geboortedatum",
    "portal.animalDetail.wizards.side.sex": "Geslacht",
    "portal.animalDetail.wizards.side.age": "Leeftijd",
    "portal.animalDetail.wizards.side.withYouSince": "Bij jou sinds",
    "portal.animalDetail.wizards.side.days": "dgn",
    "portal.animalDetail.wizards.side.summary": "Wizard samenvatting",
    "portal.animalDetail.wizards.side.statusDone": "Ingevuld",
    "portal.animalDetail.wizards.side.statusBusy": "Bezig…",
    "portal.animalDetail.wizards.side.statusWait": "Wacht op vorige stap",
    "portal.animalDetail.tasks.subtitle": "Wat er binnenkort moet gebeuren",
    "portal.animalDetail.tasks.seeAll": "Bekijk alle taken",
    "portal.animalDetail.tasks.empty": "Geen taken of aandachtspunten.",
    "portal.animalDetail.tasks.vaccPrefix": "Enting: ",
    "portal.animalDetail.tasks.repeat": "Herhaling",
    "portal.animalDetail.tasks.due.today": "Vandaag",
    "portal.animalDetail.tasks.due.in": "Over",
    "portal.animalDetail.tasks.due.day": "dag",
    "portal.animalDetail.tasks.due.days": "dagen",
    "portal.animalDetail.tasks.due.late": "te laat",
    "portal.animalDetail.seeAll": "Bekijk alle",
    "portal.animalDetail.seeLess": "Toon minder",
    // ── 8.3 beschikbaarheid ────────────────────────────────────────────────
    "portal.availability.add": "Nieuwe afwezigheid",
    "portal.availability.title": "Beschikbaarheid",
    "portal.availability.subtitle":
      "Beheer hier eenvoudig je geplande afwezigheden zodat de vestigingen weten wanneer je niet beschikbaar bent.",
    "portal.availability.stat.planned.title": "Geplande afwezigheden",
    "portal.availability.stat.planned.sub": "Komende periode",
    "portal.availability.stat.total.title": "Totaal afwezig",
    "portal.availability.stat.total.sub": "Dit jaar",
    "portal.availability.stat.validFor.title": "Geldig voor",
    "portal.availability.allLocations": "Alle vestigingen",
    "portal.availability.card.planned": "Geplande afwezigheden",
    "portal.availability.card.next": "Eerstvolgende afwezigheid",
    "portal.availability.column.locations": "Vestigingen",
    "portal.availability.edit": "Bewerken",
    "portal.availability.badge.planned": "Gepland",
    "portal.availability.badge.active": "Actief",
    "portal.availability.next.note": "Vestigingen kunnen je in deze periode niet toewijzen.",
    "portal.availability.next.empty": "Geen geplande afwezigheid.",
    "portal.availability.days.one": "dag",
    "portal.availability.days.many": "dagen",
    "portal.availability.dialog.titleEdit": "Afwezigheid bewerken",
    "portal.availability.empty": "Je hebt geen geplande afwezigheid.",
    "portal.availability.column.period": "Periode",
    "portal.availability.column.type": "Type",
    "portal.availability.column.source": "Bron",
    "portal.availability.column.actions": "Acties",
    "portal.availability.rangeSeparator": "t/m",
    "portal.availability.delete": "Verwijder",
    "portal.availability.deleting": "Bezig met verwijderen…",
    "portal.availability.source.person": "Door jou doorgegeven",
    "portal.availability.source.organization": "Door de organisatie doorgegeven",
    "portal.availability.type.vacation": "Vakantie",
    "portal.availability.type.sick": "Ziek",
    "portal.availability.type.other": "Niet beschikbaar",
    "portal.availability.dialog.title": "Afwezigheid toevoegen",
    "portal.availability.dialog.from": "Van",
    "portal.availability.dialog.to": "T/m",
    "portal.availability.dialog.type": "Type",
    "portal.availability.dialog.save": "Opslaan",
    "portal.availability.dialog.saving": "Bezig met opslaan…",
    "portal.availability.dialog.cancel": "Annuleren",
    "portal.availability.error.range": "Einddatum moet op of na de begindatum liggen.",
    "portal.availability.error.conflict":
      "Deze afwezigheid is door iemand anders gewijzigd. Ververs de pagina en probeer het opnieuw.",
    "portal.availability.error.generic": "Opslaan is mislukt. Probeer het opnieuw.",
    "portal.availability.error.deleteFailed": "Verwijderen is mislukt. Probeer het opnieuw.",
    // ── 8.4 contact ────────────────────────────────────────────────────────
    "portal.contact.title": "Contact",
    "portal.contact.intro": "Hier vind je alle asielen waar jij als pleeggezin geregistreerd bent.",
    "portal.contact.introSub":
      "Ook organisaties waar je nu geen dieren van opvangt blijven hier zichtbaar.",
    "portal.contact.badge.active": "Pleeggezin actief",
    "portal.contact.badge.noAnimals": "Geen dieren op dit moment",
    "portal.contact.call": "Bel",
    "portal.contact.mail": "E-mail",
    "portal.contact.details": "Meer details",
    "portal.contact.openingHours": "Openingstijden",
    "portal.contact.noDetails": "Geen aanvullende gegevens beschikbaar.",
    "portal.contact.disclaimer":
      "De contactgegevens en spoednummers zijn verstrekt door de organisaties en kunnen worden gewijzigd. Raadpleeg bij twijfel altijd de website van de organisatie.",
    "portal.contact.empty": "Je organisatie is nog niet gekoppeld aan het platform.",
    "portal.contact.logoAlt": "Logo van de organisatie",
    "portal.contact.fallbackName": "Organisatie",
    "portal.contact.unreachable": "Contactgegevens zijn momenteel niet beschikbaar.",
    "portal.contact.emergency.title": "Spoednummer (24/7)",
    "portal.contact.emergency.number": "06 – 123 456 78",
    "portal.contact.emergency.note": "Alleen bij spoed buiten kantooruren.",
    // ── 8.5 mijn gegevens ──────────────────────────────────────────────────
    "portal.profile.emailLabel": "E-mailadres",
    "portal.profile.organizationsTitle": "Gekoppelde organisaties",
    "portal.profile.organizationsEmpty": "Je bent nog niet aan een organisatie gekoppeld.",
    "portal.profile.organizationFallback": "Organisatie",
    // ── admin console — shared (jmix-9) ────────────────────────────────────
    "admin.common.actions": "Acties",
    "admin.common.view": "Bekijken",
    "admin.common.edit": "Bewerken",
    "admin.common.manage": "Beheren",
    "admin.common.close": "Sluiten",
    "admin.common.cancel": "Annuleren",
    "admin.common.save": "Opslaan",
    "admin.common.saving": "Bezig met opslaan…",
    "admin.common.active": "Actief",
    "admin.common.inactive": "Inactief",
    "admin.common.enabled": "Aan",
    "admin.common.disabled": "Uit",
    // ── 9.1 users ──────────────────────────────────────────────────────────
    "admin.users.empty": "Geen accounts",
    "admin.persons.empty": "Geen personen",
    "admin.persons.intro": "Personen worden vanuit Backoffice aangemaakt en kunnen hier niet worden toegevoegd of bewerkt. Hun account wordt aangemaakt bij de eerste keer inloggen via een inlogcode.",
    "admin.users.column.username": "Gebruikersnaam",
    "admin.users.column.firstName": "Voornaam",
    "admin.users.column.lastName": "Achternaam",
    "admin.users.column.email": "E-mailadres",
    "admin.users.column.timeZone": "Tijdzone",
    "admin.users.column.type": "Type",
    "admin.users.column.active": "Actief",
    "admin.users.type.account": "Account",
    "admin.users.type.person": "Persoon",
    "admin.users.type.service": "Serviceaccount",
    "admin.users.type.other": "Overig",
    "admin.users.search.label": "Zoeken",
    "admin.users.search.placeholder": "Gebruikersnaam of e-mailadres",
    "admin.users.create.open": "Nieuwe gebruiker",
    "admin.users.create.title": "Gebruiker aanmaken",
    "admin.users.create.help": "Maak een persoonlijke gebruiker aan die kan inloggen. Zet 'Beheerder' aan om volledige beheertoegang te geven; zonder die rol kan de gebruiker inloggen maar de beheeromgeving niet gebruiken.",
    "admin.users.create.admin": "Beheerder (volledige toegang)",
    "admin.users.create.password": "Wachtwoord",
    "admin.users.create.timeZone": "Tijdzone",
    "admin.users.create.error.usernameRequired": "Vul een gebruikersnaam in.",
    "admin.users.create.error.passwordWeak": "Het wachtwoord moet minstens 8 tekens bevatten.",
    "admin.users.create.error.duplicate": "Deze gebruikersnaam bestaat al.",
    "admin.users.create.error.invalid": "Controleer de ingevulde gegevens.",
    "admin.users.create.error.generic": "Aanmaken is mislukt. Probeer het opnieuw.",
    "admin.users.edit.title": "Gebruiker bewerken",
    "admin.users.edit.error.notFound": "Deze gebruiker bestaat niet meer.",
    "admin.users.edit.error.generic": "Opslaan is mislukt. Probeer het opnieuw.",
    "admin.users.password.section": "Wachtwoord",
    "admin.users.password.open": "Wachtwoord wijzigen",
    "admin.users.password.title": "Wachtwoord wijzigen",
    "admin.users.password.new": "Nieuw wachtwoord",
    "admin.users.password.confirm": "Bevestig wachtwoord",
    "admin.users.password.error.mismatch": "De twee wachtwoorden komen niet overeen.",
    "admin.users.password.error.generic": "Wijzigen is mislukt. Probeer het opnieuw.",
    "admin.users.resetLink.button": "Reset-link opnieuw uitgeven",
    "admin.users.resetLink.title": "Reset-link uitgeven",
    "admin.users.resetLink.revealTitle": "Reset-link",
    "admin.users.resetLink.revealOnce": "Deze link wordt maar één keer getoond. Kopieer en deel hem nu.",
    "admin.users.resetLink.copy": "Link kopiëren",
    "admin.users.resetLink.copied": "Link gekopieerd naar klembord.",
    "admin.users.resetLink.error": "Uitgeven van de reset-link is mislukt. Probeer het opnieuw.",
    "admin.users.delete.section": "Verwijderen",
    "admin.users.delete.open": "Gebruiker verwijderen",
    "admin.users.delete.title": "Gebruiker verwijderen",
    "admin.users.delete.warning": "Weet u het zeker? De gebruiker kan daarna niet meer inloggen.",
    "admin.users.delete.confirm": "Ja, verwijderen",
    "admin.users.delete.error.self": "Je kunt je eigen account niet verwijderen.",
    "admin.users.delete.error.generic": "Verwijderen is mislukt. Probeer het opnieuw.",
    "admin.users.column.role": "Rol",
    "admin.users.role.admin": "Beheerder",
    "admin.users.role.section": "Beheerdersrol",
    "admin.users.role.isAdmin": "Deze gebruiker heeft volledige beheertoegang.",
    "admin.users.role.notAdmin": "Deze gebruiker heeft geen beheertoegang.",
    "admin.users.role.grant": "Beheerdersrol toekennen",
    "admin.users.role.revoke": "Beheerdersrol intrekken",
    "admin.users.role.grantTitle": "Beheerdersrol toekennen",
    "admin.users.role.revokeTitle": "Beheerdersrol intrekken",
    "admin.users.role.grantWarning": "Deze gebruiker krijgt volledige toegang tot de beheeromgeving, inclusief gebruikers, serviceaccounts en organisaties. Dit gaat in bij de volgende keer inloggen.",
    "admin.users.role.revokeWarning": "Deze gebruiker verliest direct de toegang tot de beheeromgeving.",
    "admin.users.role.revokeSelfWarning": "Let op: dit is je eigen account. Je verliest direct de toegang tot dit scherm.",
    "admin.users.role.confirmGrant": "Ja, toekennen",
    "admin.users.role.confirmRevoke": "Ja, intrekken",
    "admin.users.role.error.generic": "Wijzigen van de rol is mislukt. Probeer het opnieuw.",
    "admin.users.error.lastAdmin": "Dit is de laatste actieve beheerder. Geef eerst een andere gebruiker de beheerdersrol.",
    // ── 9.2 service accounts ───────────────────────────────────────────────
    "admin.sa.empty": "Er zijn nog geen serviceaccounts.",
    "admin.sa.detail.back": "← Terug naar serviceaccounts",
    "admin.sa.column.username": "Gebruikersnaam",
    "admin.sa.column.status": "Status",
    "admin.sa.column.webPortal": "Webtoegang",
    "admin.sa.create.open": "Nieuw serviceaccount",
    "admin.sa.create.title": "Serviceaccount aanmaken",
    "admin.sa.create.help": "Geef een gebruikersnaam op. Inloggegevens (client-id en secret) maak je daarna aan.",
    "admin.sa.create.username.help": "Korte gebruikersnaam — bijv. prod-integration, staging-bot.",
    "admin.sa.create.error.required": "Vul een gebruikersnaam in.",
    "admin.sa.create.error.duplicate": "Deze gebruikersnaam bestaat al.",
    "admin.sa.create.error.generic": "Aanmaken is mislukt. Probeer het opnieuw.",
    "admin.sa.detail.webPortal": "Interactieve webtoegang (selfservice)",
    "admin.sa.detail.webPortal.help":
      "Indien ingeschakeld kan dit account inloggen op /aerie en de selfservice-portal bereiken. Geef apart een reset-link uit.",
    "admin.sa.detail.active": "Account actief",
    "admin.sa.detail.resetLink.button": "Reset-link opnieuw uitgeven",
    "admin.sa.detail.resetLink.revealTitle": "Wachtwoord-reset-link",
    "admin.sa.detail.resetLink.revealOnce":
      "Kopieer deze eenmalige link nu — hij wordt maar één keer getoond en een eerdere link is nu ongeldig.",
    "admin.sa.detail.resetLink.copy": "Link kopiëren",
    "admin.sa.detail.resetLink.copied": "Link gekopieerd naar klembord.",
    "admin.sa.detail.resetLink.error": "Uitgeven van de reset-link is mislukt. Probeer het opnieuw.",
    "admin.sa.detail.credentials": "Inloggegevens",
    "admin.sa.detail.mint.scopes": "Scopes (spatie-gescheiden)",
    "admin.sa.detail.mint.submit": "Nieuwe genereren",
    "admin.sa.detail.mint.error.scopes": "Vul ten minste één scope in.",
    "admin.sa.detail.mint.organization": "Organisatie (nieuwe credential)",
    "admin.sa.cred.organization": "Organisatie",
    "admin.sa.cred.organization.none": "— geen organisatie —",
    "admin.sa.cred.organization.unbound": "Niet gekoppeld",
    "admin.sa.cred.organization.bind": "Koppeling opslaan",
    "admin.sa.cred.organization.error": "Koppelen is mislukt. Probeer het opnieuw.",
    "admin.sa.detail.mint.error.unknownScopes":
      "Onbekende scope. Toegestaan: foster.activate, foster.deactivate, foster.update, absences.read, absences.write, nazorg.invite, publications.write.",
    "admin.sa.detail.mint.error.generic": "Genereren is mislukt. Probeer het opnieuw.",
    "admin.sa.detail.mint.revealTitle": "Nieuwe client secret",
    "admin.sa.detail.mint.revealOnce": "Kopieer hem nu — hij wordt niet opnieuw getoond.",
    "admin.sa.detail.mint.copy": "Secret kopiëren",
    "admin.sa.detail.mint.copied": "Secret gekopieerd naar klembord.",
    "admin.sa.detail.mint.copyError": "Kopiëren naar klembord is mislukt.",
    "admin.sa.detail.noCredentials": "Nog geen inloggegevens.",
    "admin.sa.detail.allowlist": "Toegestane IP-adressen",
    "admin.sa.allowlist.add.label": "CIDR toevoegen",
    "admin.sa.allowlist.add.submit": "Toevoegen",
    "admin.sa.allowlist.added": "Toegevoegd",
    "admin.sa.allowlist.remove": "Verwijderen",
    "admin.sa.allowlist.zeroWarning":
      "Geen toegestane IP-adressen geconfigureerd. Met de per-organisatie IP-allowlist aan zullen verzoeken via dit serviceaccount geweigerd worden.",
    "admin.sa.allowlist.error.required": "Vul een CIDR in.",
    "admin.sa.allowlist.error.invalid": "Geen geldig IPv4- of IPv6-CIDR.",
    "admin.sa.allowlist.error.duplicate": "Dit CIDR staat al in de lijst.",
    "admin.sa.allowlist.error.generic": "De actie is mislukt. Probeer het opnieuw.",
    "admin.sa.detail.danger": "Account",
    "admin.sa.detail.softDelete": "Account verwijderen",
    "admin.sa.detail.forceExpire": "Laten verlopen",
    "admin.sa.detail.revoke": "Intrekken",
    "admin.sa.detail.extend.label": "Verlengen met",
    "admin.sa.detail.extend.submit": "Verlengen",
    "admin.sa.detail.extend.h24": "+24 uur",
    "admin.sa.detail.extend.h72": "+72 uur",
    "admin.sa.detail.extend.w1": "+1 week",
    "admin.sa.detail.extend.mo1": "+1 maand",
    "admin.sa.detail.clearEndDate": "Einddatum wissen",
    "admin.sa.detail.expireAll.submit": "Alle laten verlopen",
    "admin.sa.detail.expireAll.confirm": "Alle inloggegevens van dit account laten verlopen?",
    "admin.sa.detail.expireAll.confirmYes": "Ja, alle laten verlopen",
    "admin.sa.detail.expireAll.result": "Aantal verlopen inloggegevens:",
    "admin.sa.detail.action.error": "De actie is mislukt. Probeer het opnieuw.",
    "admin.sa.filter.status": "Status",
    "admin.sa.filter.active": "Actief",
    "admin.sa.filter.disabled": "Uitgeschakeld",
    "admin.sa.filter.all": "Alle",
    "admin.sa.filter.removed": "Verwijderd",
    "admin.sa.status.softDeleted": "Verwijderd",
    "admin.sa.detail.restore": "Herstellen",
    "admin.sa.detail.showRevoked": "Ingetrokken inloggegevens tonen",
    "admin.sa.detail.noRevoked": "Geen ingetrokken inloggegevens.",
    "admin.sa.detail.restoreCredential": "Herstellen",
    "admin.sa.cred.clientId": "Client-id",
    "admin.sa.cred.prefix": "Secret",
    "admin.sa.cred.scopes": "Scopes",
    "admin.sa.cred.created": "Aangemaakt",
    "admin.sa.cred.lastUsed": "Laatst gebruikt",
    "admin.sa.cred.neverUsed": "Nooit gebruikt",
    "admin.sa.cred.expires": "Verloopt",
    "admin.sa.column.lastUsed": "Laatst gebruikt",
    "admin.sa.confirm.yes": "Ja, doorgaan",
    "admin.sa.confirm.softDelete":
      "Account ‘{username}’ verwijderen? Alle inloggegevens en toegestane IP-adressen worden ook verwijderd. Herstellen kan daarna, maar inloggegevens en IP-adressen komen niet automatisch terug.",
    "admin.sa.confirm.forceExpire": "Deze inloggegevens nu laten verlopen?",
    "admin.sa.confirm.clearEndDate": "De einddatum van deze inloggegevens wissen (niet meer verlopen)?",
    "admin.sa.confirm.restoreCredential": "Deze ingetrokken inloggegevens herstellen?",
    "admin.sa.confirm.restoreAccount": "Dit account herstellen?",
    "admin.sa.confirm.resetLink":
      "Een nieuwe reset-link uitgeven? Een eventueel eerdere link wordt hiermee ongeldig.",
    // ── 9.2 service-account self-service portal (operator-facing) ──────────
    "selfservice.intro":
      "Bekijk de gegevens van je serviceaccount en beheer je eigen OAuth2-inloggegevens.",
    "selfservice.allowedIps.added": "Toegevoegd",
    "selfservice.identity.title": "Identiteit",
    "selfservice.identity.username": "Gebruikersnaam",
    "selfservice.identity.active": "Actief",
    "selfservice.allowedIps.title": "Toegestane IP-adressen",
    "selfservice.allowedIps.intro":
      "Alleen-lezen — neem contact op met een beheerder om de toegestane IP-reeksen te wijzigen.",
    "selfservice.allowedIps.empty": "Geen IP-beperking ingesteld.",
    "selfservice.credentials.title": "Inloggegevens",
    "selfservice.credentials.intro":
      "Genereer een nieuwe credential en trek een oude in om zonder downtime te roteren.",
    "selfservice.credentials.generate": "Credential genereren",
    "selfservice.credentials.empty": "Nog geen inloggegevens.",
    "selfservice.credentials.revoke": "Intrekken",
    "selfservice.generate.confirmTitle": "Nieuwe credential genereren",
    "selfservice.generate.confirmBody":
      "Er wordt een nieuw client_id + client_secret uitgegeven. De secret wordt één keer getoond — kopieer hem voordat je het venster sluit.",
    "selfservice.generate.confirm": "Genereren",
    "selfservice.secretOnce.title": "Nieuwe inloggegevens",
    "selfservice.secretOnce.intro": "Kopieer de secret nu — hij wordt maar één keer getoond.",
    "selfservice.secretOnce.clientId": "Client-id",
    "selfservice.secretOnce.secret": "Client secret",
    "selfservice.secretOnce.warning":
      "Bewaar de secret veilig. Hij kan daarna niet opnieuw worden opgehaald.",
    "selfservice.secretOnce.copy": "Secret kopiëren",
    "selfservice.secretOnce.copied": "Secret gekopieerd naar klembord.",
    "selfservice.secretOnce.close": "Sluiten",
    "selfservice.error.generate": "Genereren is mislukt. Probeer het opnieuw.",
    "selfservice.error.revoke": "Intrekken is mislukt. Probeer het opnieuw.",
    "selfservice.error.copy": "Kopiëren naar klembord is mislukt.",
    "selfservice.error.noCredential":
      "Er is geen bestaande credential om te roteren. Neem contact op met een beheerder.",
    // ── 9.3 organizations ───────────────────────────────────────────────────────
    "admin.organizations.empty": "Er zijn nog geen organisaties.",
    "admin.organizations.selectRow": "Rij selecteren",
    "admin.organizations.selectAll": "Alles selecteren",
    "admin.organizations.column.displayName": "Naam",
    "admin.organizations.column.clientId": "Client-id",
    "admin.organizations.column.baseUrl": "Basis-URL",
    "admin.pairing.title": "Backoffice-koppelingen",
    "admin.pairing.help":
      "Een koppelcode laat een backoffice zichzelf aanmelden: plak de code in de backoffice onder Instellingen → Organisatie → Platformkoppeling. De code is 15 minuten geldig en werkt één keer.",
    "admin.pairing.open": "Backoffice koppelen",
    "admin.pairing.reopen": "Opnieuw koppelen",
    "admin.pairing.dialog.title": "Backoffice koppelen",
    "admin.pairing.dialog.displayName": "Naam (optioneel)",
    "admin.pairing.dialog.backofficeUrl": "Adres van de backoffice",
    "admin.pairing.dialog.backofficeUrl.help":
      "Bijv. https://roze.docasoft.nl. Alleen een backoffice op precies dit adres kan de code gebruiken.",
    "admin.pairing.dialog.scopes": "Rechten",
    "admin.pairing.dialog.submit": "Koppelcode maken",
    "admin.pairing.dialog.error.url": "Vul het adres van de backoffice in.",
    "admin.pairing.dialog.error.invalid": "Het adres of de rechten zijn niet geldig.",
    "admin.pairing.dialog.error.generic": "Het maken van de koppelcode is mislukt. Probeer het opnieuw.",
    "admin.pairing.code.title": "Koppelcode",
    "admin.pairing.code.once": "Kopieer de code nu — hij wordt niet opnieuw getoond. Geldig tot",
    "admin.pairing.code.copy": "Code kopiëren",
    "admin.pairing.code.copied": "Code gekopieerd naar het klembord.",
    "admin.pairing.code.copyError": "Kopiëren naar het klembord is mislukt.",
    "admin.pairing.column.status": "Status",
    "admin.pairing.column.name": "Naam",
    "admin.pairing.column.origin": "Backoffice",
    "admin.pairing.column.until": "Geldig tot",
    "admin.pairing.revoke": "Intrekken",
    "admin.pairing.revokeError": "Intrekken is mislukt. Probeer het opnieuw.",
    "admin.pairing.empty": "Nog geen koppelcodes uitgegeven.",
    "admin.pairing.emptyOpen": "Geen actieve koppelingen.",
    "admin.pairing.showClosed": "Toon verlopen en ingetrokken",
    "admin.pairing.error": "De koppelingen konden niet worden geladen.",
    "admin.pairing.status.ISSUED": "Code uitgegeven",
    "admin.pairing.status.REDEEMED": "Wacht op bevestiging",
    "admin.pairing.status.CONFIRMED": "Gekoppeld",
    "admin.pairing.status.EXPIRED": "Verlopen",
    "admin.pairing.status.REVOKED": "Ingetrokken",
    "admin.organizations.credentials.title": "Gekoppelde credentials",
    "admin.organizations.credentials.help":
      "Alleen deze credentials mogen namens deze organisatie optreden. Een ongekoppelde credential mag dat nog zolang binding niet verplicht is.",
    "admin.organizations.credentials.serviceAccount": "Serviceaccount",
    "admin.organizations.credentials.empty": "Nog geen credential aan deze organisatie gekoppeld.",
    "admin.organizations.credentials.error": "De gekoppelde credentials konden niet worden geladen.",
    "admin.organizations.column.id": "Organisatie-ID",
    "admin.organizations.help.id":
      "Stel deze waarde in als fawkes.platform.organization-uuid in de backoffice van deze organisatie.",
    "admin.organizations.create.open": "Nieuwe organisatie",
    "admin.organizations.create.title": "Organisatie aanmaken",
    "admin.organizations.create.clientSecret": "Client secret (eenmalig zichtbaar)",
    "admin.organizations.create.error.required": "Vul Basis-URL, Client-id en Client secret in.",
    "admin.organizations.create.error.duplicate": "Dit Client-id bestaat al.",
    "admin.organizations.create.error.generic": "Aanmaken is mislukt. Probeer het opnieuw.",
    "admin.organizations.create.error.noKeys":
      "Versleutelingssleutels niet geconfigureerd; stel fawkes.platform.organization-secret-{key,salt} in.",
    "admin.organizations.help.displayName": "Optioneel; label voor de operator.",
    "admin.organizations.help.baseUrl": "Bijv. https://organisatie-a.example. Geen afsluitende slash.",
    "admin.organizations.help.clientId": "De OAuth2-client_id die de backoffice heeft uitgegeven.",
    "admin.organizations.help.clientSecret":
      "De plaintext wordt versleuteld vóór opslag en is daarna niet meer leesbaar.",
    "admin.organizations.detail.edit": "Identiteit bewerken",
    "admin.organizations.detail.save": "Opslaan",
    "admin.organizations.detail.edited": "Organisatie bijgewerkt.",
    "admin.organizations.detail.edit.error.required": "Vul Basis-URL en Client-id in.",
    "admin.organizations.detail.edit.error.duplicate": "Dit Client-id is al in gebruik door een andere organisatie.",
    "admin.organizations.detail.edit.error.generic": "Bijwerken is mislukt. Probeer het opnieuw.",
    "admin.organizations.rotate.open": "Client secret roteren",
    "admin.organizations.rotate.title": "Client secret roteren",
    "admin.organizations.rotate.newSecret": "Nieuwe client secret",
    "admin.organizations.rotate.hint": "Voer de nieuwe client secret in. Deze wordt versleuteld opgeslagen en daarna niet meer getoond.",
    "admin.organizations.rotate.submit": "Roteren",
    "admin.organizations.rotate.success": "Client secret geroteerd. De tokencache van deze organisatie is geleegd.",
    "admin.organizations.rotate.error.required": "Voer een nieuwe client secret in.",
    "admin.organizations.rotate.error.generic": "Roteren is mislukt. Probeer het opnieuw.",
    "admin.organizations.bulk.delete": "Geselecteerde verwijderen",
    "admin.organizations.bulk.evict": "Tokencache legen",
    "admin.organizations.bulk.deleted": "Verwijderd",
    "admin.organizations.bulk.evicted": "Cache geleegd",
    "admin.organizations.bulk.error": "De bulkactie is mislukt. Probeer het opnieuw.",
    "admin.organizations.bulk.result.succeeded": "verwerkt",
    "admin.organizations.bulk.result.skipped": "overgeslagen (al verwijderd)",
    "admin.organizations.bulk.confirm.deleteTitle": "Geselecteerde organisaties verwijderen?",
    "admin.organizations.bulk.confirm.deleteCount": "organisaties zacht verwijderen?",
    "admin.organizations.bulk.confirm.deleteBody":
      "Bestaande koppelingen blijven leesbaar, maar uitgaande aanroepen geven NotFound tot een organisatie wordt hersteld.",
    "admin.organizations.bulk.confirm.deleteYes": "Ja, verwijderen",
    "admin.organizations.bulk.confirm.evictTitle": "Tokencache legen?",
    "admin.organizations.bulk.confirm.evictCount": "Gecachte tokens legen voor",
    "admin.organizations.bulk.confirm.evictCountSuffix": "organisaties?",
    "admin.organizations.bulk.confirm.evictBody":
      "De volgende uitgaande aanroep haalt een vers token op.",
    "admin.organizations.bulk.confirm.evictYes": "Ja, cache legen",
    "admin.organizations.history.title": "Wijzigingshistorie",
    "admin.organizations.history.when": "Tijdstip",
    "admin.organizations.history.actor": "Gebruiker",
    "admin.organizations.history.attribute": "Veld",
    "admin.organizations.history.old": "Oude waarde",
    "admin.organizations.history.new": "Nieuwe waarde",
    "admin.organizations.history.empty": "Geen wijzigingen vastgelegd.",
    "admin.organizations.history.error": "De historie kon niet worden geladen.",
    "admin.organizations.testConnection.button": "Verbinding testen",
    "admin.organizations.testConnection.testing": "Bezig met testen…",
    "admin.organizations.testConnection.ok": "Verbinding OK",
    "admin.organizations.testConnection.failed": "Verbinding mislukt",
    "admin.organizations.testConnection.hint":
      "Controleer de basis-URL, client_id, client secret en de IP-allowlist van de backoffice.",
    "admin.organizations.testConnection.error": "De verbindingstest kon niet worden uitgevoerd. Probeer het opnieuw.",
    "admin.organizations.column.status": "Status",
    "admin.organizations.status.softDeleted": "Verwijderd",
    "admin.organizations.filter.status": "Status",
    "admin.organizations.filter.active": "Actief",
    "admin.organizations.filter.removed": "Verwijderd",
    "admin.organizations.filter.all": "Alle",
    "admin.organizations.detail.softDelete": "Organisatie verwijderen",
    "admin.organizations.detail.restore": "Herstellen",
    "admin.organizations.detail.action.error": "De actie is mislukt. Probeer het opnieuw.",
    "admin.organizations.detail.confirm.deleteTitle": "Organisatie verwijderen?",
    "admin.organizations.detail.confirm.deleteBody":
      "Bestaande koppelingen blijven leesbaar, maar uitgaande aanroepen geven NotFound tot deze organisatie wordt hersteld.",
    "admin.organizations.detail.confirm.deleteYes": "Ja, verwijderen",
    "admin.organizations.detail.confirm.restoreTitle": "Organisatie herstellen?",
    "admin.organizations.detail.confirm.restoreBody": "Deze organisatie wordt weer actief.",
    "admin.organizations.detail.confirm.restoreYes": "Ja, herstellen",
    // ── 9.3 master-key rotation ────────────────────────────────────────────
    "admin.rotation.title": "Master key roteren",
    "admin.rotation.intro":
      "Dit versleutelt de uitgaande secrets van alle organisaties opnieuw met een nieuwe master key. Voer dit alleen uit na overleg.",
    "admin.rotation.newKey": "Nieuwe master key",
    "admin.rotation.newKey.help": "De nieuwe master-passphrase waarmee elk uitgaand secret opnieuw wordt versleuteld.",
    "admin.rotation.newSalt": "Nieuwe salt (hex)",
    "admin.rotation.newSalt.help": "Hex-gecodeerde salt die bij de nieuwe sleutel hoort (zoals Encryptors.stronger verwacht).",
    "admin.rotation.confirm": "Typ ter bevestiging: rotate-organization-keys",
    "admin.rotation.submit": "Nu roteren",
    "admin.rotation.rotating": "Bezig met roteren…",
    "admin.rotation.error": "Roteren is mislukt. Probeer het opnieuw.",
    "admin.rotation.affected": "Bijgewerkt",
    "admin.rotation.outcome.success": "Gelukt",
    "admin.rotation.outcome.alreadyRotated": "Al geroteerd",
    "admin.rotation.outcome.failed": "Mislukt",
    "admin.rotation.success.lead": "rijen opnieuw versleuteld & geverifieerd. Werk de deployment-config bij en schakel daarna over naar Herladen.",
    "admin.rotation.alreadyRotated.detail":
      "Alle rijen ontsleutelen al onder de opgegeven sleutels; er is geen rotatie uitgevoerd.",
    "admin.rotation.failed.detail": "Rotatie teruggedraaid; geen enkele rij is opnieuw versleuteld.",
    "admin.rotation.verified": "rijen opnieuw versleuteld & geverifieerd",
    // ── A.4 in-place encryptor reload (POST reload-encryption-keys) ─────────
    "admin.rotation.mode.label": "Modus",
    "admin.rotation.mode.rotate": "Nu roteren (transactionele her-versleuteling)",
    "admin.rotation.mode.reload": "Sleutels herladen uit omgeving (geen rij-aanpassingen)",
    "admin.reload.intro":
      "Herlaad de live encryptor onder de nieuwe sleutel/salt uit de omgeving — zonder herstart. Doe dit pas nadat de rijen zijn geroteerd én de deployment-config is bijgewerkt.",
    "admin.reload.confirm": "Typ ter bevestiging: reload-encryption-keys",
    "admin.reload.submit": "Sleutels herladen",
    "admin.reload.reloading": "Bezig met herladen…",
    "admin.reload.error": "Herladen is mislukt. Probeer het opnieuw.",
    "admin.reload.outcome.success": "Gelukt",
    "admin.reload.outcome.no_change": "Geen wijziging",
    "admin.reload.outcome.failed": "Validatie mislukt",
    "admin.reload.outcome.disabled": "Uitgeschakeld",
    "admin.reload.success.lead": "Versleuteling draait nu onder de nieuwe sleutels.",
    "admin.reload.success.evicted":
      "token-cache-vermeldingen gewist; de volgende uitgaande aanroep mint verse tokens.",
    "admin.reload.noChange.detail":
      "De kandidaat-sleutels gaven hetzelfde ontsleutel-gedrag als de live encryptor. Meestal betekent dit dat de config-update de draaiende JVM niet bereikte. Controleer die stap en probeer opnieuw.",
    "admin.reload.failed.detail":
      "De sleutels konden de opgeslagen ciphertext niet ontsleutelen. De live encryptor is ONGEWIJZIGD — uitgaande aanroepen werken nog onder de vorige sleutels.",
    "admin.reload.disabled.detail":
      "De omgeving levert geen sleutelmateriaal. Herladen is een no-op en de encryptor blijft ongeconfigureerd.",
  },
  en: {
    // ── menu.xml (ported) ──────────────────────────────────────────────────
    "menu.application.title": "Foster Family Portal",
    "menu.dashboard": "Dashboard",
    "menu.myAnimals": "My animals",
    "menu.availability": "Availability",
    "menu.contact": "Contact",
    "menu.myProfile": "My profile",
    "menu.selfservice.title": "Self-service",
    "menu.selfservice.myServiceAccount": "My service account",
    "menu.security.title": "Security",
    "menu.users": "Users",
    "menu.persons": "Persons",
    "menu.serviceAccounts": "Service accounts",
    "menu.organizations": "Organizations",
    "menu.organizationRotation": "Rotate master key",
    // ── shell chrome ───────────────────────────────────────────────────────
    "shell.brand": "Docasoft",
    "shell.applicationTitle": "Foster Family Portal",
    "shell.applicationTitle.selfservice": "Service account portal",
    "shell.applicationTitle.admin": "Administration",
    "shell.profile.settings": "Settings",
    "shell.profile.logout": "Log out",
    "shell.role.foster": "Foster parent",
    "shell.role.admin": "Administrator",
    "shell.role.self-service": "Service account",
    "shell.home.title": "Overview",
    "shell.home.body": "Welcome. Pick a section in the menu on the left.",
    "shell.underConstruction.title": "Under construction",
    "shell.underConstruction.body": "This screen is built in a later phase.",
    "shell.underConstruction.home": "Back to overview",
    // ── auth screens (jmix-3b.1) ───────────────────────────────────────────
    "auth.admin.title": "Administrator sign-in",
    "auth.admin.lede": "Sign in with your username and password.",
    "auth.admin.submit": "Sign in",
    "auth.admin.submitting": "Signing in…",
    "auth.admin.rememberMe": "Remember me",
    "auth.foster.title": "Sign in to the foster portal",
    "auth.foster.eyebrow": "For foster families of animal shelters",
    "auth.foster.emailLede": "Enter your email address and receive a sign-in code.",
    "auth.foster.trust": "Access for foster families",
    "auth.foster.requestCode": "Send code",
    "auth.foster.requesting": "Sending…",
    "auth.foster.codeSentPrefix": "If ",
    "auth.foster.codeSentSuffix":
      " is known to us, you'll receive a sign-in code by email within a few minutes.",
    "auth.foster.codeTitle": "Enter your sign-in code",
    "auth.foster.codeLede": "Enter the code from the email.",
    "auth.foster.devCode": "Test environment — your login code is:",
    "auth.foster.verify": "Sign in",
    "auth.foster.verifying": "Checking…",
    "auth.foster.welcomeBack": "Welcome back.",
    "auth.foster.changeEmail": "Use a different email address",
    "auth.foster.resend": "Send a new code",
    "auth.foster.resending": "Sending…",
    // ── set-password (jmix-7.3, ported from the legacy SetPasswordView bundle) ──
    "auth.setPassword.title": "Set new password",
    "auth.setPassword.heading": "Set a new password",
    "auth.setPassword.intro":
      "Choose a password of at least 8 characters. After saving you can sign in with the new password.",
    "auth.setPassword.submit": "Save password",
    "auth.setPassword.submitting": "Saving…",
    "auth.setPassword.success": "Password set. You can now sign in.",
    "auth.setPassword.toLogin": "Go to sign in",
    "auth.setPassword.error.uniform":
      "Link expired or invalid. Ask the administrator to reissue a fresh reset link.",
    "auth.setPassword.error.confirmMismatch": "The two passwords do not match.",
    "auth.setPassword.error.weakPassword": "Password must be at least 8 characters long.",
    "auth.setPassword.help.newPassword": "At least 8 characters.",
    "auth.field.username": "Username",
    "auth.field.password": "Password",
    "auth.field.newPassword": "New password",
    "auth.field.confirmPassword": "Confirm new password",
    "auth.field.email": "Email address",
    "auth.field.code": "Sign-in code",
    "auth.field.revealPassword": "Show password",
    "auth.field.hidePassword": "Hide password",
    "auth.error.network": "Could not reach the server. Please try again.",
    "auth.error.generic": "Sign-in failed. Please try again.",
    "auth.error.rateLimited": "Too many attempts. Please wait and try again.",
    "auth.error.invalidCredentials": "Invalid username or password.",
    "auth.error.invalidCode": "The code is incorrect or has expired.",
    "auth.error.codeFormat": "The sign-in code is six digits.",
    "auth.error.enterEmail": "Enter an email address.",
    "auth.error.enterCode": "Enter the sign-in code.",
    "auth.error.enterCredentials": "Enter your username and password.",
    "auth.error.enterPassword": "Enter a new password.",
    // ── error / empty / loading conventions ────────────────────────────────
    "state.loading": "Loading…",
    "state.empty.title": "Nothing to show",
    "state.empty.body": "There is no data yet.",
    "state.error.title": "Something went wrong",
    "state.error.body": "This screen could not be loaded. Please try again.",
    "state.error.retry": "Try again",
    "state.placeholder.note": "This section arrives in a later migration phase.",
    // ── nazorg adopter portal (aftercare questionnaire) ────────────────────
    "nazorg.portalName": "Aftercare after adoption",
    "nazorg.fallbackName": "your new pet",
    "nazorg.saveLater": "Save & continue later",
    "nazorg.savedToast": "Your answers are saved — you can continue later.",
    "nazorg.welcome.kicker": "Welcome back",
    "nazorg.welcome.title": "How is {naam} doing?",
    "nazorg.welcome.lede":
      "Thank you for giving {naam} a new home. We'd love to hear how things are going together — your answers help us improve our care.",
    "nazorg.welcome.count": "{n} short questions",
    "nazorg.welcome.duration": "± 3 minutes",
    "nazorg.welcome.savedNote": "We've kept your earlier answers.",
    "nazorg.start": "Start",
    "nazorg.resume": "Continue",
    "nazorg.context.kicker": "Your companion",
    "nazorg.context.subtitle": "We like to follow how {naam} is doing at home.",
    "nazorg.context.sectionMeta": "{x} of {y} answered",
    "nazorg.context.helpTitle": "Questions or concerns?",
    "nazorg.question": "Question",
    "nazorg.autosave": "Your answers are saved automatically",
    "nazorg.next": "Next",
    "nazorg.back": "Back",
    "nazorg.finish": "Finish",
    "nazorg.onepage.subtitle": "Fill in what you'd like to share — all questions are optional.",
    "nazorg.onepage.answered": "{x} / {total} answered",
    "nazorg.submit": "Submit questionnaire",
    "nazorg.submitting": "Sending…",
    "nazorg.submitError": "Sending failed. Please try again in a moment.",
    "nazorg.done.title": "Thank you!",
    "nazorg.done.lede":
      "Your answers have been sent. Thank you for taking the time to let us know how {naam} is doing. We'll get in touch if needed.",
    "nazorg.done.review": "Review answers again",
    "nazorg.expired.title": "Link no longer valid",
    "nazorg.expired.body":
      "This questionnaire has already been completed or the link has expired. Please contact the shelter if you have questions.",
    "nazorg.foto.choose": "Choose photo",
    "nazorg.foto.replace": "Choose another photo",
    "nazorg.foto.remove": "Remove",
    "nazorg.foto.uploading": "Uploading…",
    "nazorg.foto.previewAlt": "Chosen photo",
    "nazorg.foto.error": "Upload failed. Please choose a JPEG or PNG photo (max 8 MB).",
    // ── foster portal — shared (jmix-8) ────────────────────────────────────
    "portal.backofficeUnavailable":
      "The organization's data is currently unavailable. Please try again later.",
    // ── 8.1 dashboard ──────────────────────────────────────────────────────
    "portal.dashboard.welcome": "Welcome",
    "portal.dashboard.subtitle":
      "Glad you're here! Below are the animals you are caring for right now.",
    "portal.dashboard.animals.title": "My animals",
    "portal.dashboard.animals.empty": "You have no animals in your care right now.",
    "portal.dashboard.since": "Since",
    "portal.dashboard.updates.title": "Recent updates",
    "portal.dashboard.updates.empty": "No recent updates yet.",
    "portal.dashboard.updates.weight": "Weight",
    "portal.dashboard.updates.unit.kg": "kg",
    "portal.dashboard.updates.byPerson": "By you",
    "portal.dashboard.updates.byOrganization": "By the organization",
    "portal.dashboard.allAnimals": "Go to all my animals",
    "portal.dashboard.allUpdates": "Go to all updates",
    "portal.dashboard.viewDetails": "View details",
    "portal.dashboard.vestiging": "Location",
    "portal.dashboard.time.today": "Today",
    "portal.dashboard.time.yesterday": "Yesterday",
    "portal.dashboard.availability.title": "Availability",
    "portal.dashboard.availability.next": "Next absence",
    "portal.dashboard.availability.none": "No planned absence.",
    "portal.dashboard.availability.planned": "Planned",
    "portal.dashboard.availability.allOrganizations": "Applies to all organizations",
    "portal.dashboard.availability.add": "Add new absence",
    "portal.dashboard.contact.title": "Organization contact",
    "portal.dashboard.contact.unavailable": "Contact details are currently unavailable.",
    "portal.dashboard.contact.heading": "Contact & emergency",
    "portal.dashboard.contact.daytime": "Daytime contact",
    "portal.dashboard.allLocations": "All locations",
    "portal.dashboard.updates.add": "Add a new update",
    "portal.dashboard.availability.toAvailability": "To availability",
    "portal.dashboard.thanks.title": "Thank you!",
    "portal.dashboard.thanks.body":
      "Thanks to your effort these animals get the best care and a chance at a wonderful new home.",
    // ── 8.2 mijn dieren ────────────────────────────────────────────────────
    "portal.animals.empty": "You have no animals in your care right now.",
    "portal.animals.photoAlt": "Photo of the animal",
    "portal.animals.photoAltNamed": "Photo of ",
    "portal.animals.dobLabel": "Date of birth",
    "portal.animals.chipLabel": "Chip number",
    "portal.animals.title": "My animals",
    "portal.animals.subtitle": "Below are all the animals currently in your care.",
    "portal.animals.stat.total": "Total animals",
    "portal.animals.stat.lastUpdated": "Last updated",
    "portal.animals.search": "Search by name or breed…",
    "portal.animals.filter.allSpecies": "All animals",
    "portal.animals.sort.newest": "Sort: Newest first",
    "portal.animals.sort.oldest": "Sort: Oldest first",
    "portal.animals.sort.name": "Sort: Name A–Z",
    "portal.animals.status.since": "In care since ",
    "portal.animals.open": "Open",
    "portal.animals.noResults": "No animals found.",
    "portal.error.title": "Oops, something went technically wrong",
    "portal.error.body1": "We're looking into the error.",
    "portal.error.body2": "Feel free to try again.",
    "portal.error.retry": "Try again",
    "portal.error.home": "Back to dashboard",
    "portal.error.footerPrefix": "Still not working? Then",
    "portal.error.contactLink": "contact us",
    // ── 8.5 animal detail ──────────────────────────────────────────────────
    "portal.animalDetail.back": "Back to my animals",
    "portal.animalDetail.nav.prev": "Previous animal",
    "portal.animalDetail.nav.next": "Next animal",
    "portal.animalDetail.nav.of": "of",
    "portal.animalDetail.tabs.label": "Sections",
    "portal.animalDetail.tabs.overview": "Overview",
    "portal.animalDetail.tabs.care": "Care",
    "portal.animalDetail.tabs.medical": "Medical",
    "portal.animalDetail.tabs.notes": "Notes",
    "portal.animalDetail.tabs.log": "Log",
    "portal.animalDetail.actions.title": "Actions",
    "portal.animalDetail.actions.addNote": "New note",
    "portal.animalDetail.actions.addWeight": "Add weight",
    "portal.animalDetail.actions.addFeeding": "Log feeding",
    "portal.animalDetail.actions.addVacc": "New vaccination",
    "portal.animalDetail.actions.editFeeding": "Edit feeding plan",
    "portal.animalDetail.stat.stayDuration": "Length of stay",
    "portal.animalDetail.stat.day": "day",
    "portal.animalDetail.stat.days": "days",
    "portal.animalDetail.overview.weight": "Weight",
    "portal.animalDetail.overview.latestMeasurement": "Latest measurement",
    "portal.animalDetail.overview.weightValue": "Weight",
    "portal.animalDetail.overview.medical": "Medical attention points",
    "portal.animalDetail.overview.medicalEmpty": "No vaccinations or treatments recorded.",
    "portal.animalDetail.overview.recentNotes": "Recent notes",
    "portal.animalDetail.overview.seeAllMedical": "See all",
    "portal.animalDetail.overview.seeAllNotes": "See all",
    "portal.animalDetail.overview.last": "Last:",
    "portal.animalDetail.log.title": "Log",
    "portal.animalDetail.log.empty": "Nothing has been recorded for this animal yet.",
    "portal.animalDetail.log.kind.note": "Note",
    "portal.animalDetail.log.kind.weight": "Weight measured",
    "portal.animalDetail.log.kind.feeding": "Feeding",
    "portal.animalDetail.log.kind.vaccination": "Vaccination / treatment",
    "portal.animalDetail.tile.basis": "Basic information",
    "portal.animalDetail.tile.stay": "Stay",
    "portal.animalDetail.tile.weights": "Weights",
    "portal.animalDetail.tile.notes": "Notes",
    "portal.animalDetail.tile.protection": "Protection",
    "portal.animalDetail.tile.feeding": "Feeding",
    "portal.animalDetail.tile.media": "Media & documents",
    "portal.animalDetail.notAvailable": "Not available via the portal yet",
    "portal.animalDetail.field.species": "Species",
    "portal.animalDetail.field.breed": "Breed",
    "portal.animalDetail.field.sex": "Sex",
    "portal.animalDetail.field.dob": "Date of birth",
    "portal.animalDetail.field.chip": "Chip number",
    "portal.animalDetail.stay.since": "Since",
    "portal.animalDetail.stay.until": "Until",
    "portal.animalDetail.stay.open": "present",
    "portal.animalDetail.weights.empty": "No weights recorded yet.",
    "portal.animalDetail.weights.unit": "kg",
    "portal.animalDetail.weights.add": "Add weight",
    "portal.animalDetail.weights.kg": "Weight (kg)",
    "portal.animalDetail.weights.kgPlaceholder": "e.g. 17.5",
    "portal.animalDetail.weights.latest": "Latest measurement:",
    "portal.animalDetail.weights.columnKg": "Weight (kg)",
    "portal.animalDetail.weights.period3m": "Last 3 months",
    "portal.animalDetail.weights.period1m": "Last month",
    "portal.animalDetail.weights.periodAll": "All",
    "portal.animalDetail.weights.periodFew": "Too few measurements in this period — showing all.",
    "portal.animalDetail.weights.columnSource": "Source",
    "portal.animalDetail.notes.empty": "No notes yet.",
    "portal.animalDetail.notes.addNew": "New note",
    "portal.animalDetail.notes.titleLabel": "Title",
    "portal.animalDetail.notes.textLabel": "Note",
    "portal.animalDetail.notes.pinned": "Pinned",
    "portal.animalDetail.notes.tagsLabel": "Tags",
    "portal.animalDetail.notes.add": "Add note",
    "portal.animalDetail.notes.delete": "Delete",
    "portal.animalDetail.notes.deleteConfirm": "Are you sure you want to delete this note?",
    "portal.animalDetail.notes.titlePlaceholder": "Title",
    "portal.animalDetail.notes.textPlaceholder": "Note…",
    "portal.animalDetail.bySource.person": "By you",
    "portal.animalDetail.bySource.organization": "By the organization",
    "portal.animalDetail.tile.tasks": "Tasks & attention points",
    "portal.animalDetail.tile.registrations": "Registrations & wizards",
    "portal.animalDetail.field.name": "Name",
    "portal.animalDetail.field.caster": "Neutered/spayed",
    "portal.animalDetail.field.castrationDate": "Neutering date",
    "portal.animalDetail.field.coat": "Coat",
    "portal.animalDetail.field.coatColor": "Coat color",
    "portal.animalDetail.field.passport": "Passport number",
    "portal.animalDetail.field.pedigree": "Pedigree number",
    "portal.animalDetail.field.deceased": "Date deceased",
    "portal.animalDetail.field.features": "Appearance",
    "portal.animalDetail.field.tags": "Tags",
    "portal.animalDetail.field.since": "In foster care since",
    "portal.animalDetail.age.years": "y",
    "portal.animalDetail.age.months": "mo",
    "portal.animalDetail.yes": "Yes",
    "portal.animalDetail.no": "No",
    "portal.animalDetail.field.crossbreed": "Crossbreed",
    "portal.animalDetail.protection.empty": "No records.",
    "portal.animalDetail.protection.next": "Next:",
    "portal.animalDetail.protection.givenBy": "By:",
    "portal.animalDetail.protection.status.actueel": "Current",
    "portal.animalDetail.protection.status.binnenkort": "Due soon",
    "portal.animalDetail.protection.status.verlopen": "Expired",
    "portal.animalDetail.protection.status.onbekend": "Unknown",
    "portal.animalDetail.edit.edit": "Edit",
    "portal.animalDetail.edit.save": "Save",
    "portal.animalDetail.edit.saving": "Saving…",
    "portal.animalDetail.edit.cancel": "Cancel",
    "portal.animalDetail.edit.error": "Saving failed. Please try again.",
    "portal.animalDetail.feeding.empty": "No feeding plan available.",
    "portal.animalDetail.feeding.feedType": "Feed type",
    "portal.animalDetail.feeding.brand": "Brand",
    "portal.animalDetail.feeding.amount": "Amount",
    "portal.animalDetail.feeding.times": "Feeding times",
    "portal.animalDetail.feeding.diet": "Diet",
    "portal.animalDetail.feeding.note": "Note",
    "portal.animalDetail.feeding.log": "Feeding log",
    "portal.animalDetail.feeding.editSchema": "Edit plan",
    "portal.animalDetail.feeding.addLog": "Add",
    "portal.animalDetail.feeding.column.date": "Date",
    "portal.animalDetail.feeding.amountGrams": "Amount (grams)",
    "portal.animalDetail.feeding.behavior": "Behavior",
    "portal.animalDetail.feeding.behaviorPlaceholder": "Behavior — choose one",
    "portal.animalDetail.feeding.amountRequired": "Enter an amount (grams) greater than 0.",
    "portal.animalDetail.feeding.logDelete": "Delete",
    "portal.animalDetail.feeding.logDeleteConfirm": "Are you sure you want to delete this log entry?",
    "portal.animalDetail.media.empty": "No media or documents.",
    "portal.animalDetail.media.description": "Description",
    "portal.animalDetail.media.delete": "Delete",
    "portal.animalDetail.media.deleteConfirm": "Are you sure you want to delete this file?",
    "portal.animalDetail.vacc.add": "Add vaccination",
    "portal.animalDetail.vacc.delete": "Delete",
    "portal.animalDetail.vacc.deleteConfirm": "Are you sure you want to delete this vaccination?",
    "portal.animalDetail.vacc.name": "Type",
    "portal.animalDetail.vacc.brand": "Product/brand",
    "portal.animalDetail.vacc.date": "Given on",
    "portal.animalDetail.vacc.nextDate": "Next on",
    "portal.animalDetail.vacc.addEnting": "New vaccination",
    "portal.animalDetail.vacc.addParasite": "New deworming/deflea",
    "portal.animalDetail.vacc.parasitePreset": "Deworming/deflea",
    "portal.animalDetail.vacc.filter.all": "All",
    "portal.animalDetail.vacc.filter.enting": "Vaccination",
    "portal.animalDetail.vacc.filter.ontwormen": "Deworming",
    "portal.animalDetail.vacc.filter.ontvlooien": "Deflea",
    "portal.animalDetail.vacc.filter.search": "Search...",
    "portal.animalDetail.vacc.filter.clear": "Clear filters",
    "portal.animalDetail.vacc.filter.none": "No results for these filters.",
    "portal.animalDetail.vacc.aiTitle": "Read passport with AI",
    "portal.animalDetail.vacc.aiHint": "Upload photos or PDFs of the pet passport; the AI extracts the vaccinations for you.",
    "portal.animalDetail.vacc.aiRead": "Read all vaccinations (AI)",
    "portal.animalDetail.vacc.aiClear": "Clear uploads",
    "portal.animalDetail.vacc.aiBusy": "Reading...",
    "portal.animalDetail.vacc.aiImport": "Import",
    "portal.animalDetail.vacc.aiNone": "No vaccinations found on the pages.",
    "portal.animalDetail.vacc.aiNotConfigured": "The AI reader is not configured on this environment.",
    "portal.animalDetail.vacc.aiError": "Reading failed. Please try again.",
    "portal.animalDetail.vacc.aiFoundPrefix": "Found:",
    "portal.animalDetail.vacc.aiFoundSuffix": "entries. Tick the ones to import.",
    "portal.animalDetail.wizards.empty": "No wizards available.",
    "portal.animalDetail.wizards.start": "Start",
    "portal.animalDetail.wizards.started": "✓ Started",
    "portal.animalDetail.wizards.finished2": "✓ Completed",
    "portal.animalDetail.wizards.previous": "Previous",
    "portal.animalDetail.wizards.next": "Next",
    "portal.animalDetail.wizards.finish": "Finish",
    "portal.animalDetail.wizards.saveError": "Saving failed. Please try again.",
    "portal.animalDetail.wizards.required": "Please fill in:",
    "portal.animalDetail.wizards.requiredSuffix": "is required.",
    "portal.animalDetail.wizards.noSchema": "This wizard has no questions (yet).",
    "portal.animalDetail.wizards.noFields": "No fields in this step.",
    "portal.animalDetail.wizards.yes": "Yes",
    "portal.animalDetail.wizards.no": "No",
    "portal.animalDetail.wizards.run": "run",
    "portal.animalDetail.wizards.saveDraft": "Save as draft",
    "portal.animalDetail.wizards.side.chip": "Chip number",
    "portal.animalDetail.wizards.side.birth": "Date of birth",
    "portal.animalDetail.wizards.side.sex": "Sex",
    "portal.animalDetail.wizards.side.age": "Age",
    "portal.animalDetail.wizards.side.withYouSince": "With you since",
    "portal.animalDetail.wizards.side.days": "days",
    "portal.animalDetail.wizards.side.summary": "Wizard summary",
    "portal.animalDetail.wizards.side.statusDone": "Completed",
    "portal.animalDetail.wizards.side.statusBusy": "In progress…",
    "portal.animalDetail.wizards.side.statusWait": "Waiting for previous step",
    "portal.animalDetail.tasks.subtitle": "What needs to happen soon",
    "portal.animalDetail.tasks.seeAll": "See all tasks",
    "portal.animalDetail.tasks.empty": "No tasks or attention points.",
    "portal.animalDetail.tasks.vaccPrefix": "Vaccination: ",
    "portal.animalDetail.tasks.repeat": "Repeat",
    "portal.animalDetail.tasks.due.today": "Today",
    "portal.animalDetail.tasks.due.in": "In",
    "portal.animalDetail.tasks.due.day": "day",
    "portal.animalDetail.tasks.due.days": "days",
    "portal.animalDetail.tasks.due.late": "overdue",
    "portal.animalDetail.seeAll": "See all",
    "portal.animalDetail.seeLess": "Show less",
    // ── 8.3 beschikbaarheid ────────────────────────────────────────────────
    "portal.availability.add": "New absence",
    "portal.availability.title": "Availability",
    "portal.availability.subtitle":
      "Manage your planned absences here so the locations know when you're unavailable.",
    "portal.availability.stat.planned.title": "Planned absences",
    "portal.availability.stat.planned.sub": "Upcoming",
    "portal.availability.stat.total.title": "Total absent",
    "portal.availability.stat.total.sub": "This year",
    "portal.availability.stat.validFor.title": "Valid for",
    "portal.availability.allLocations": "All locations",
    "portal.availability.card.planned": "Planned absences",
    "portal.availability.card.next": "Next absence",
    "portal.availability.column.locations": "Locations",
    "portal.availability.edit": "Edit",
    "portal.availability.badge.planned": "Planned",
    "portal.availability.badge.active": "Active",
    "portal.availability.next.note": "Locations can't assign animals to you during this period.",
    "portal.availability.next.empty": "No planned absence.",
    "portal.availability.days.one": "day",
    "portal.availability.days.many": "days",
    "portal.availability.dialog.titleEdit": "Edit absence",
    "portal.availability.empty": "You have no planned absence.",
    "portal.availability.column.period": "Period",
    "portal.availability.column.type": "Type",
    "portal.availability.column.source": "Source",
    "portal.availability.column.actions": "Actions",
    "portal.availability.rangeSeparator": "to",
    "portal.availability.delete": "Delete",
    "portal.availability.deleting": "Deleting…",
    "portal.availability.source.person": "Reported by you",
    "portal.availability.source.organization": "Reported by the organization",
    "portal.availability.type.vacation": "Holiday",
    "portal.availability.type.sick": "Sick",
    "portal.availability.type.other": "Unavailable",
    "portal.availability.dialog.title": "Add absence",
    "portal.availability.dialog.from": "From",
    "portal.availability.dialog.to": "Until",
    "portal.availability.dialog.type": "Type",
    "portal.availability.dialog.save": "Save",
    "portal.availability.dialog.saving": "Saving…",
    "portal.availability.dialog.cancel": "Cancel",
    "portal.availability.error.range": "The end date must be on or after the start date.",
    "portal.availability.error.conflict":
      "This absence was changed by someone else. Refresh the page and try again.",
    "portal.availability.error.generic": "Saving failed. Please try again.",
    "portal.availability.error.deleteFailed": "Deleting failed. Please try again.",
    // ── 8.4 contact ────────────────────────────────────────────────────────
    "portal.contact.title": "Contact",
    "portal.contact.intro": "All shelters where you are registered as a foster family.",
    "portal.contact.introSub":
      "Organizations you are not currently fostering for remain visible here.",
    "portal.contact.badge.active": "Foster active",
    "portal.contact.badge.noAnimals": "No animals at the moment",
    "portal.contact.call": "Call",
    "portal.contact.mail": "E-mail",
    "portal.contact.details": "More details",
    "portal.contact.openingHours": "Opening hours",
    "portal.contact.noDetails": "No additional details available.",
    "portal.contact.disclaimer":
      "Contact details and emergency numbers are provided by the organizations and may change. When in doubt, always check the organization's website.",
    "portal.contact.empty": "Your organization is not yet linked to the platform.",
    "portal.contact.logoAlt": "Organization logo",
    "portal.contact.fallbackName": "Organization",
    "portal.contact.unreachable": "Contact details are currently unavailable.",
    "portal.contact.emergency.title": "Emergency number (24/7)",
    "portal.contact.emergency.number": "06 – 123 456 78",
    "portal.contact.emergency.note": "For emergencies outside office hours only.",
    // ── 8.5 mijn gegevens ──────────────────────────────────────────────────
    "portal.profile.emailLabel": "Email address",
    "portal.profile.organizationsTitle": "Linked organizations",
    "portal.profile.organizationsEmpty": "You are not linked to an organization yet.",
    "portal.profile.organizationFallback": "Organization",
    // ── admin console — shared (jmix-9) ────────────────────────────────────
    "admin.common.actions": "Actions",
    "admin.common.view": "View",
    "admin.common.edit": "Edit",
    "admin.common.manage": "Manage",
    "admin.common.close": "Close",
    "admin.common.cancel": "Cancel",
    "admin.common.save": "Save",
    "admin.common.saving": "Saving…",
    "admin.common.active": "Active",
    "admin.common.inactive": "Inactive",
    "admin.common.enabled": "On",
    "admin.common.disabled": "Off",
    // ── 9.1 users ──────────────────────────────────────────────────────────
    "admin.users.empty": "No accounts",
    "admin.persons.empty": "No persons",
    "admin.persons.intro": "Persons are created from Backoffice and cannot be added or edited here. Their account is provisioned on their first magic-code sign-in.",
    "admin.users.column.username": "Username",
    "admin.users.column.firstName": "First name",
    "admin.users.column.lastName": "Last name",
    "admin.users.column.email": "Email address",
    "admin.users.column.timeZone": "Time zone",
    "admin.users.column.type": "Type",
    "admin.users.column.active": "Active",
    "admin.users.type.account": "Account",
    "admin.users.type.person": "Person",
    "admin.users.type.service": "Service account",
    "admin.users.type.other": "Other",
    "admin.users.search.label": "Search",
    "admin.users.search.placeholder": "Username or email address",
    "admin.users.create.open": "New user",
    "admin.users.create.title": "Create user",
    "admin.users.create.help": "Create a personal user that can sign in. Turn on 'Admin' to give full admin access; without that role the user can sign in but cannot use the admin console.",
    "admin.users.create.admin": "Admin (full access)",
    "admin.users.create.password": "Password",
    "admin.users.create.timeZone": "Time zone",
    "admin.users.create.error.usernameRequired": "Enter a username.",
    "admin.users.create.error.passwordWeak": "The password must be at least 8 characters.",
    "admin.users.create.error.duplicate": "That username already exists.",
    "admin.users.create.error.invalid": "Please check the details you entered.",
    "admin.users.create.error.generic": "Creating failed. Please try again.",
    "admin.users.edit.title": "Edit user",
    "admin.users.edit.error.notFound": "This user no longer exists.",
    "admin.users.edit.error.generic": "Saving failed. Please try again.",
    "admin.users.password.section": "Password",
    "admin.users.password.open": "Change password",
    "admin.users.password.title": "Change password",
    "admin.users.password.new": "New password",
    "admin.users.password.confirm": "Confirm password",
    "admin.users.password.error.mismatch": "The two passwords do not match.",
    "admin.users.password.error.generic": "Changing the password failed. Please try again.",
    "admin.users.resetLink.button": "Reissue reset link",
    "admin.users.resetLink.title": "Issue reset link",
    "admin.users.resetLink.revealTitle": "Reset link",
    "admin.users.resetLink.revealOnce": "This link is shown only once. Copy and share it now.",
    "admin.users.resetLink.copy": "Copy link",
    "admin.users.resetLink.copied": "Link copied to clipboard.",
    "admin.users.resetLink.error": "Issuing the reset link failed. Please try again.",
    "admin.users.delete.section": "Delete",
    "admin.users.delete.open": "Delete user",
    "admin.users.delete.title": "Delete user",
    "admin.users.delete.warning": "Are you sure? The user will no longer be able to sign in.",
    "admin.users.delete.confirm": "Yes, delete",
    "admin.users.delete.error.self": "You cannot delete your own account.",
    "admin.users.delete.error.generic": "Deleting failed. Please try again.",
    "admin.users.column.role": "Role",
    "admin.users.role.admin": "Admin",
    "admin.users.role.section": "Admin role",
    "admin.users.role.isAdmin": "This user has full admin access.",
    "admin.users.role.notAdmin": "This user has no admin access.",
    "admin.users.role.grant": "Grant admin role",
    "admin.users.role.revoke": "Revoke admin role",
    "admin.users.role.grantTitle": "Grant admin role",
    "admin.users.role.revokeTitle": "Revoke admin role",
    "admin.users.role.grantWarning": "This user gets full access to the admin console, including users, service accounts and organizations. It takes effect at their next sign-in.",
    "admin.users.role.revokeWarning": "This user immediately loses access to the admin console.",
    "admin.users.role.revokeSelfWarning": "Careful: this is your own account. You immediately lose access to this screen.",
    "admin.users.role.confirmGrant": "Yes, grant",
    "admin.users.role.confirmRevoke": "Yes, revoke",
    "admin.users.role.error.generic": "Changing the role failed. Please try again.",
    "admin.users.error.lastAdmin": "This is the last active admin. Grant the admin role to another user first.",
    // ── 9.2 service accounts ───────────────────────────────────────────────
    "admin.sa.empty": "There are no service accounts yet.",
    "admin.sa.detail.back": "← Back to service accounts",
    "admin.sa.column.username": "Username",
    "admin.sa.column.status": "Status",
    "admin.sa.column.webPortal": "Web access",
    "admin.sa.create.open": "New service account",
    "admin.sa.create.title": "Create service account",
    "admin.sa.create.help": "Enter a username. Credentials (client id and secret) are issued afterwards.",
    "admin.sa.create.username.help": "Short username identifier — e.g. prod-integration, staging-bot.",
    "admin.sa.create.error.required": "Enter a username.",
    "admin.sa.create.error.duplicate": "That username already exists.",
    "admin.sa.create.error.generic": "Creating failed. Please try again.",
    "admin.sa.detail.webPortal": "Interactive web access (self-service)",
    "admin.sa.detail.webPortal.help":
      "When enabled, this account can log in at /aerie and reach the self-service portal. Issue a reset link separately.",
    "admin.sa.detail.active": "Account active",
    "admin.sa.detail.resetLink.button": "Reissue reset link",
    "admin.sa.detail.resetLink.revealTitle": "Password-reset link",
    "admin.sa.detail.resetLink.revealOnce":
      "Copy this single-use link now — it is shown only once and any prior link is now invalid.",
    "admin.sa.detail.resetLink.copy": "Copy link",
    "admin.sa.detail.resetLink.copied": "Link copied to clipboard.",
    "admin.sa.detail.resetLink.error": "Issuing the reset link failed. Please try again.",
    "admin.sa.detail.credentials": "Credentials",
    "admin.sa.detail.mint.scopes": "Scopes (space-separated)",
    "admin.sa.detail.mint.submit": "Generate new",
    "admin.sa.detail.mint.error.scopes": "Enter at least one scope.",
    "admin.sa.detail.mint.organization": "Organization (new credential)",
    "admin.sa.cred.organization": "Organization",
    "admin.sa.cred.organization.none": "— no organization —",
    "admin.sa.cred.organization.unbound": "Not bound",
    "admin.sa.cred.organization.bind": "Save binding",
    "admin.sa.cred.organization.error": "Binding failed. Please try again.",
    "admin.sa.detail.mint.error.unknownScopes":
      "Unknown scope. Allowed: foster.activate, foster.deactivate, foster.update, absences.read, absences.write, nazorg.invite, publications.write.",
    "admin.sa.detail.mint.error.generic": "Generating failed. Please try again.",
    "admin.sa.detail.mint.revealTitle": "New client secret",
    "admin.sa.detail.mint.revealOnce": "Copy it now — it will not be shown again.",
    "admin.sa.detail.mint.copy": "Copy secret",
    "admin.sa.detail.mint.copied": "Secret copied to clipboard.",
    "admin.sa.detail.mint.copyError": "Copying to the clipboard failed.",
    "admin.sa.detail.noCredentials": "No credentials yet.",
    "admin.sa.detail.allowlist": "Allowed IP addresses",
    "admin.sa.allowlist.add.label": "Add CIDR",
    "admin.sa.allowlist.add.submit": "Add",
    "admin.sa.allowlist.added": "Added",
    "admin.sa.allowlist.remove": "Remove",
    "admin.sa.allowlist.zeroWarning":
      "No allowed IPs configured. With the per-organization IP allowlist enabled, callers using this service account will be denied.",
    "admin.sa.allowlist.error.required": "Enter a CIDR.",
    "admin.sa.allowlist.error.invalid": "Not a valid IPv4 or IPv6 CIDR.",
    "admin.sa.allowlist.error.duplicate": "This CIDR is already on the list.",
    "admin.sa.allowlist.error.generic": "The action failed. Please try again.",
    "admin.sa.detail.danger": "Account",
    "admin.sa.detail.softDelete": "Delete account",
    "admin.sa.detail.forceExpire": "Force expire",
    "admin.sa.detail.revoke": "Revoke",
    "admin.sa.detail.extend.label": "Extend by",
    "admin.sa.detail.extend.submit": "Extend",
    "admin.sa.detail.extend.h24": "+24 hours",
    "admin.sa.detail.extend.h72": "+72 hours",
    "admin.sa.detail.extend.w1": "+1 week",
    "admin.sa.detail.extend.mo1": "+1 month",
    "admin.sa.detail.clearEndDate": "Clear end date",
    "admin.sa.detail.expireAll.submit": "Expire all",
    "admin.sa.detail.expireAll.confirm": "Expire all credentials for this account?",
    "admin.sa.detail.expireAll.confirmYes": "Yes, expire all",
    "admin.sa.detail.expireAll.result": "Credentials expired:",
    "admin.sa.detail.action.error": "The action failed. Please try again.",
    "admin.sa.filter.status": "Status",
    "admin.sa.filter.active": "Active",
    "admin.sa.filter.disabled": "Disabled",
    "admin.sa.filter.all": "All",
    "admin.sa.filter.removed": "Soft-deleted",
    "admin.sa.status.softDeleted": "Soft-deleted",
    "admin.sa.detail.restore": "Restore",
    "admin.sa.detail.showRevoked": "Show revoked credentials",
    "admin.sa.detail.noRevoked": "No revoked credentials.",
    "admin.sa.detail.restoreCredential": "Restore",
    "admin.sa.cred.clientId": "Client id",
    "admin.sa.cred.prefix": "Secret",
    "admin.sa.cred.scopes": "Scopes",
    "admin.sa.cred.created": "Created",
    "admin.sa.cred.lastUsed": "Last used",
    "admin.sa.cred.neverUsed": "Never used",
    "admin.sa.cred.expires": "Expires",
    "admin.sa.column.lastUsed": "Last used",
    "admin.sa.confirm.yes": "Yes, continue",
    "admin.sa.confirm.softDelete":
      "Soft-delete ‘{username}’? All credentials and allowed IPs are soft-deleted too. Restore is available afterwards, but credentials and IPs do not come back automatically.",
    "admin.sa.confirm.forceExpire": "Force-expire this credential now?",
    "admin.sa.confirm.clearEndDate": "Clear this credential's end date (make it non-expiring)?",
    "admin.sa.confirm.restoreCredential": "Restore this revoked credential?",
    "admin.sa.confirm.restoreAccount": "Restore this account?",
    "admin.sa.confirm.resetLink":
      "Reissue a reset link? Any prior outstanding link will be invalidated.",
    // ── 9.2 service-account self-service portal (operator-facing) ──────────
    "selfservice.intro":
      "View your service-account details and manage your own OAuth2 credentials.",
    "selfservice.allowedIps.added": "Added",
    "selfservice.identity.title": "Identity",
    "selfservice.identity.username": "Username",
    "selfservice.identity.active": "Active",
    "selfservice.allowedIps.title": "Allowed IPs",
    "selfservice.allowedIps.intro":
      "Read-only — contact an administrator to change the allowed IP ranges.",
    "selfservice.allowedIps.empty": "No IP restriction set.",
    "selfservice.credentials.title": "Credentials",
    "selfservice.credentials.intro":
      "Mint a fresh credential and revoke an old one to rotate without downtime.",
    "selfservice.credentials.generate": "Generate credential",
    "selfservice.credentials.empty": "No credentials yet.",
    "selfservice.credentials.revoke": "Revoke",
    "selfservice.generate.confirmTitle": "Generate new credential",
    "selfservice.generate.confirmBody":
      "A new client_id + client_secret will be issued. The secret is shown once — copy it before closing the dialog.",
    "selfservice.generate.confirm": "Generate",
    "selfservice.secretOnce.title": "New credential",
    "selfservice.secretOnce.intro": "Copy the secret now — it is shown only once.",
    "selfservice.secretOnce.clientId": "Client id",
    "selfservice.secretOnce.secret": "Client secret",
    "selfservice.secretOnce.warning": "Store the secret securely. It cannot be retrieved again.",
    "selfservice.secretOnce.copy": "Copy secret",
    "selfservice.secretOnce.copied": "Secret copied to clipboard.",
    "selfservice.secretOnce.close": "Close",
    "selfservice.error.generate": "Generating failed. Please try again.",
    "selfservice.error.revoke": "Revoking failed. Please try again.",
    "selfservice.error.copy": "Copying to the clipboard failed.",
    "selfservice.error.noCredential":
      "There is no existing credential to rotate. Contact an administrator.",
    // ── 9.3 organizations ───────────────────────────────────────────────────────
    "admin.organizations.empty": "There are no organizations yet.",
    "admin.organizations.selectRow": "Select row",
    "admin.organizations.selectAll": "Select all",
    "admin.organizations.column.displayName": "Name",
    "admin.organizations.column.clientId": "Client id",
    "admin.organizations.column.baseUrl": "Base URL",
    "admin.pairing.title": "Backoffice pairings",
    "admin.pairing.help":
      "A pairing code lets a backoffice register itself: paste the code in the backoffice under Settings → Organization → Platform connection. The code is valid for 15 minutes and works once.",
    "admin.pairing.open": "Pair a backoffice",
    "admin.pairing.reopen": "Pair again",
    "admin.pairing.dialog.title": "Pair a backoffice",
    "admin.pairing.dialog.displayName": "Name (optional)",
    "admin.pairing.dialog.backofficeUrl": "Backoffice address",
    "admin.pairing.dialog.backofficeUrl.help":
      "E.g. https://roze.docasoft.nl. Only a backoffice at exactly this address can use the code.",
    "admin.pairing.dialog.scopes": "Permissions",
    "admin.pairing.dialog.submit": "Create pairing code",
    "admin.pairing.dialog.error.url": "Enter the backoffice address.",
    "admin.pairing.dialog.error.invalid": "The address or the permissions are not valid.",
    "admin.pairing.dialog.error.generic": "Creating the pairing code failed. Please try again.",
    "admin.pairing.code.title": "Pairing code",
    "admin.pairing.code.once": "Copy the code now — it will not be shown again. Valid until",
    "admin.pairing.code.copy": "Copy code",
    "admin.pairing.code.copied": "Code copied to clipboard.",
    "admin.pairing.code.copyError": "Copying to the clipboard failed.",
    "admin.pairing.column.status": "Status",
    "admin.pairing.column.name": "Name",
    "admin.pairing.column.origin": "Backoffice",
    "admin.pairing.column.until": "Valid until",
    "admin.pairing.revoke": "Revoke",
    "admin.pairing.revokeError": "Revoking failed. Please try again.",
    "admin.pairing.empty": "No pairing codes issued yet.",
    "admin.pairing.emptyOpen": "No active pairings.",
    "admin.pairing.showClosed": "Show expired and revoked",
    "admin.pairing.error": "The pairings could not be loaded.",
    "admin.pairing.status.ISSUED": "Code issued",
    "admin.pairing.status.REDEEMED": "Awaiting confirmation",
    "admin.pairing.status.CONFIRMED": "Paired",
    "admin.pairing.status.EXPIRED": "Expired",
    "admin.pairing.status.REVOKED": "Revoked",
    "admin.organizations.credentials.title": "Bound credentials",
    "admin.organizations.credentials.help":
      "Only these credentials may act for this organization. An unbound credential still may while binding is not required.",
    "admin.organizations.credentials.serviceAccount": "Service account",
    "admin.organizations.credentials.empty": "No credential is bound to this organization yet.",
    "admin.organizations.credentials.error": "The bound credentials could not be loaded.",
    "admin.organizations.column.id": "Organization ID",
    "admin.organizations.help.id":
      "Set this value as fawkes.platform.organization-uuid in this organization's backoffice.",
    "admin.organizations.create.open": "New organization",
    "admin.organizations.create.title": "Create organization",
    "admin.organizations.create.clientSecret": "Client secret (visible once)",
    "admin.organizations.create.error.required": "Enter Base URL, Client id and Client secret.",
    "admin.organizations.create.error.duplicate": "That Client id already exists.",
    "admin.organizations.create.error.generic": "Creating failed. Please try again.",
    "admin.organizations.create.error.noKeys":
      "Encryption keys not configured; set fawkes.platform.organization-secret-{key,salt}.",
    "admin.organizations.help.displayName": "Optional; operator label.",
    "admin.organizations.help.baseUrl": "E.g. https://organization-a.example. No trailing slash.",
    "admin.organizations.help.clientId": "The OAuth2 client_id the backoffice issued.",
    "admin.organizations.help.clientSecret":
      "Plaintext is encrypted before persistence and never readable afterwards.",
    "admin.organizations.detail.edit": "Edit identity",
    "admin.organizations.detail.save": "Save",
    "admin.organizations.detail.edited": "Organization updated.",
    "admin.organizations.detail.edit.error.required": "Enter Base URL and Client id.",
    "admin.organizations.detail.edit.error.duplicate": "That Client id is already used by another organization.",
    "admin.organizations.detail.edit.error.generic": "Updating failed. Please try again.",
    "admin.organizations.rotate.open": "Rotate client secret",
    "admin.organizations.rotate.title": "Rotate client secret",
    "admin.organizations.rotate.newSecret": "New client secret",
    "admin.organizations.rotate.hint": "Enter the new client secret. It is stored encrypted and never shown again.",
    "admin.organizations.rotate.submit": "Rotate",
    "admin.organizations.rotate.success": "Client secret rotated. This organization's token cache was cleared.",
    "admin.organizations.rotate.error.required": "Enter a new client secret.",
    "admin.organizations.rotate.error.generic": "Rotation failed. Please try again.",
    "admin.organizations.bulk.delete": "Delete selected",
    "admin.organizations.bulk.evict": "Evict token cache",
    "admin.organizations.bulk.deleted": "Deleted",
    "admin.organizations.bulk.evicted": "Cache evicted",
    "admin.organizations.bulk.error": "The bulk action failed. Please try again.",
    "admin.organizations.bulk.result.succeeded": "succeeded",
    "admin.organizations.bulk.result.skipped": "skipped (already removed)",
    "admin.organizations.bulk.confirm.deleteTitle": "Soft-delete selected organizations?",
    "admin.organizations.bulk.confirm.deleteCount": "organizations to soft-delete?",
    "admin.organizations.bulk.confirm.deleteBody":
      "Existing links stay readable, but outbound calls return NotFound until an organization is restored.",
    "admin.organizations.bulk.confirm.deleteYes": "Yes, delete",
    "admin.organizations.bulk.confirm.evictTitle": "Evict token cache?",
    "admin.organizations.bulk.confirm.evictCount": "Evict cached tokens for",
    "admin.organizations.bulk.confirm.evictCountSuffix": "organizations?",
    "admin.organizations.bulk.confirm.evictBody":
      "Next outbound call refetches a fresh token.",
    "admin.organizations.bulk.confirm.evictYes": "Yes, evict",
    "admin.organizations.history.title": "Change history",
    "admin.organizations.history.when": "When",
    "admin.organizations.history.actor": "User",
    "admin.organizations.history.attribute": "Field",
    "admin.organizations.history.old": "Old value",
    "admin.organizations.history.new": "New value",
    "admin.organizations.history.empty": "No changes recorded.",
    "admin.organizations.history.error": "The history could not be loaded.",
    "admin.organizations.testConnection.button": "Test connection",
    "admin.organizations.testConnection.testing": "Testing…",
    "admin.organizations.testConnection.ok": "Connection OK",
    "admin.organizations.testConnection.failed": "Connection failed",
    "admin.organizations.testConnection.hint":
      "Check the base URL, client_id, client secret, and the backoffice's IP allowlist.",
    "admin.organizations.testConnection.error": "The connection test could not be run. Please try again.",
    "admin.organizations.column.status": "Status",
    "admin.organizations.status.softDeleted": "Soft-deleted",
    "admin.organizations.filter.status": "Status",
    "admin.organizations.filter.active": "Active",
    "admin.organizations.filter.removed": "Soft-deleted",
    "admin.organizations.filter.all": "All",
    "admin.organizations.detail.softDelete": "Delete organization",
    "admin.organizations.detail.restore": "Restore",
    "admin.organizations.detail.action.error": "The action failed. Please try again.",
    "admin.organizations.detail.confirm.deleteTitle": "Soft-delete this organization?",
    "admin.organizations.detail.confirm.deleteBody":
      "Existing links stay readable, but outbound calls return NotFound until this organization is restored.",
    "admin.organizations.detail.confirm.deleteYes": "Yes, delete",
    "admin.organizations.detail.confirm.restoreTitle": "Restore this organization?",
    "admin.organizations.detail.confirm.restoreBody": "This organization becomes active again.",
    "admin.organizations.detail.confirm.restoreYes": "Yes, restore",
    // ── 9.3 master-key rotation ────────────────────────────────────────────
    "admin.rotation.title": "Rotate master key",
    "admin.rotation.intro":
      "This re-encrypts every organization's outbound secret under a new master key. Only perform this after coordination.",
    "admin.rotation.newKey": "New master key",
    "admin.rotation.newKey.help": "The new master passphrase every outbound secret is re-encrypted under.",
    "admin.rotation.newSalt": "New salt (hex)",
    "admin.rotation.newSalt.help": "Hex-encoded salt paired with the new key (as Encryptors.stronger expects).",
    "admin.rotation.confirm": "Type to confirm: rotate-organization-keys",
    "admin.rotation.submit": "Rotate now",
    "admin.rotation.rotating": "Rotating…",
    "admin.rotation.error": "Rotation failed. Please try again.",
    "admin.rotation.affected": "Updated",
    "admin.rotation.outcome.success": "Success",
    "admin.rotation.outcome.alreadyRotated": "Already rotated",
    "admin.rotation.outcome.failed": "Failed",
    "admin.rotation.success.lead": "rows re-encrypted & verified. Update the deployment config then switch to Reload.",
    "admin.rotation.alreadyRotated.detail":
      "All rows already decrypt under the supplied keys; no rotation performed.",
    "admin.rotation.failed.detail": "Rotation rolled back; no rows re-encrypted.",
    "admin.rotation.verified": "rows re-encrypted & verified",
    // ── A.4 in-place encryptor reload (POST reload-encryption-keys) ─────────
    "admin.rotation.mode.label": "Mode",
    "admin.rotation.mode.rotate": "Rotate now (transactional re-encryption)",
    "admin.rotation.mode.reload": "Reload encryption keys from env (no row writes)",
    "admin.reload.intro":
      "Reload the live encryptor under the new key/salt from the environment — no restart. Only do this after the rows have been rotated AND the deployment config has been updated.",
    "admin.reload.confirm": "Type to confirm: reload-encryption-keys",
    "admin.reload.submit": "Reload keys",
    "admin.reload.reloading": "Reloading…",
    "admin.reload.error": "Reload failed. Please try again.",
    "admin.reload.outcome.success": "Success",
    "admin.reload.outcome.no_change": "No change",
    "admin.reload.outcome.failed": "Validation failed",
    "admin.reload.outcome.disabled": "Disabled",
    "admin.reload.success.lead": "Encryption now live under the new keys.",
    "admin.reload.success.evicted":
      "token-cache entries evicted; the next outbound call will mint fresh tokens.",
    "admin.reload.noChange.detail":
      "The candidate keys produced the same decryption behaviour as the live delegate. Most commonly the config update did not propagate to the running JVM. Re-check that step, then retry.",
    "admin.reload.failed.detail":
      "The keys can't decrypt the stored ciphertext. The runtime encryptor is UNCHANGED — outbound calls still work under the previous keys.",
    "admin.reload.disabled.detail":
      "The environment supplies no key material. Reload is a no-op and the encryptor stays unconfigured.",
  },
};
